import jsPDF from 'jspdf';
import clinicInfo, {
  clinicAddressLine,
  clinicContactLine,
  clinicLegalLine,
  clinicLocationLine,
  reportSystemLine
} from '../config/clinicInfo';

import { Assessment, Patient } from "../types";

interface CPETInterpretation {
  functionalCapacity: {
    classification: 'Normal' | 'Levemente reduzida' | 'Moderadamente reduzida' | 'Gravemente reduzida';
    percentPredicted: number;
    description: string;
  };
  limitations: {
    cardiovascular: {
      present: boolean;
      severity: 'Ausente' | 'Leve' | 'Moderada' | 'Grave';
      evidence: string[];
    };
    ventilatory: {
      present: boolean;
      severity: 'Ausente' | 'Leve' | 'Moderada' | 'Grave';
      evidence: string[];
    };
    peripheral: {
      present: boolean;
      severity: 'Ausente' | 'Leve' | 'Moderada' | 'Grave';
      evidence: string[];
    };
    deconditioning: {
      present: boolean;
      evidence: string[];
    };
  };
  primaryLimitation: string;
  trainingZones: {
    zone1: { name: string; hrRange: string; description: string };
    zone2: { name: string; hrRange: string; description: string };
    zone3: { name: string; hrRange: string; description: string };
    zone4: { name: string; hrRange: string; description: string };
    zone5: { name: string; hrRange: string; description: string };
  };
  recommendations: string[];
  clinicalSummary: string;
  maximalTest: {
    isMaximal: boolean | null;
    criteriaMet: string[];
  };
  abnormalResponse: {
    present: boolean | null;
    criteriaMet: string[];
  };
  terminationNotes?: string | null;
}

interface MetricCard {
  label: string;
  value: string;
  details?: string | string[];
  accentColor?: [number, number, number];
}

interface InfoColumnItem {
  label: string;
  value: string;
}

interface HistoricalDataEntry {
  label: string;
  value: number;
  color?: [number, number, number];
}

export class AssessmentPDFService {
  private static idCounter = 0; // Adiciona um contador para gerar IDs únicos

  private static isPFSDQPayload(value: any): boolean {
    return Boolean(
      value &&
      typeof value === 'object' &&
      (
        value.dyspnea ||
        value.fatigue ||
        value.change ||
        typeof value.total === 'number' ||
        typeof value.totalScore === 'number'
      )
    );
  }

  private static extractPFSDQPayload(assessment: Assessment): any {
    const data = assessment.data;
    if (!data || typeof data !== 'object') return null;

    const nestedCandidates = [
      data['PFSDQ-M'],
      (data as any).PFSDQ_M,
      (data as any).PFSDQM,
      (data as any).data?.['PFSDQ-M'],
      (data as any).data?.PFSDQ_M,
      (data as any).data?.PFSDQM,
    ];

    const nestedMatch = nestedCandidates.find((candidate) => this.isPFSDQPayload(candidate));
    if (nestedMatch) return nestedMatch;

    return this.isPFSDQPayload(data) ? data : null;
  }

  // Gera um ID único para cada Assessment se não tiver um
  private static generateUniqueId(): string {
    return `temp-assessment-${this.idCounter++}-${Date.now()}`;
  }

  private static async loadLogoAsBase64(): Promise<string | null> {
    try {
      const response = await fetch(clinicInfo.reportBranding.logoPrint);
      const blob = await response.blob();

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn('Não foi possível carregar o logo:', error);
      return null;
    }
  }

  /**
   * Renderiza o cabeçalho padrão com logo e informações da clínica
   */
  private static async renderHeader(doc: jsPDF, yPosition: number): Promise<number> {
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoBase64 = await this.loadLogoAsBase64();

    // Renderizar logo centralizado com dimensões proporcionais (equivalente a 280px HTML)
    if (logoBase64) {
      try {
        const logoWidth = 52;
        const logoHeight = 44;
        const logoX = (pageWidth - logoWidth) / 2;

        doc.addImage(logoBase64, 'PNG', logoX, yPosition, logoWidth, logoHeight);
        yPosition += logoHeight + 5; // Espaço após o logo
      } catch (error) {
        console.warn('Erro ao adicionar logo ao PDF:', error);
        yPosition += 25; // Espaço reservado mesmo sem logo
      }
    } else {
      yPosition += 25; // Espaço reservado caso o logo não carregue
    }

    // Informações da clínica centralizadas (sem nome, pois já está no logo)
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(102, 102, 102);

    doc.text(clinicAddressLine, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 4;

    doc.text(clinicLocationLine, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 4;

    doc.text(clinicContactLine, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 4;

    doc.text(`CNPJ: ${clinicInfo.cnpj}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 6;

    // Linha separadora
    doc.setDrawColor(37, 99, 235); // Cor azul (equivalente a #2563eb)
    doc.setLineWidth(0.5);
    doc.line(15, yPosition, pageWidth - 15, yPosition);

    return yPosition + 10;
  }

  /**
   * Renderiza texto SpO2 com subscrito correto para PDF
   */
  private static renderSpO2Text(doc: jsPDF, x: number, y: number, align: 'left' | 'center' | 'right' = 'left'): void {
    const currentFontSize = doc.getFontSize();
    const subscriptSize = currentFontSize * 0.7;

    // Medir largura de "SpO"
    const spoWidth = doc.getTextWidth('SpO');

    let xPos = x;
    if (align === 'center') {
      const totalWidth = spoWidth + doc.getTextWidth('2') * 0.7;
      xPos = x - totalWidth / 2;
    } else if (align === 'right') {
      const totalWidth = spoWidth + doc.getTextWidth('2') * 0.7;
      xPos = x - totalWidth;
    }

    // Desenhar "SpO"
    doc.text('SpO', xPos, y);

    // Desenhar "2" como subscrito
    doc.setFontSize(subscriptSize);
    doc.text('2', xPos + spoWidth, y + 1);

    // Restaurar tamanho original
    doc.setFontSize(currentFontSize);
  }

  /**
   * Renderiza um título de seção com linha divisória
   */
  private static renderSectionTitle(doc: jsPDF, yPosition: number, title: string): number {
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(37, 99, 235);
    doc.text(title.toUpperCase(), 15, yPosition);

    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.4);
    doc.line(15, yPosition + 2, pageWidth - 15, yPosition + 2);

    return yPosition + 8;
  }

  /**
   * Desenha texto com suporte a subscritos (ex.: VO₂)
   */
  private static renderTextWithSubscripts(
    doc: jsPDF,
    text: string,
    x: number,
    y: number,
    align: 'left' | 'center' | 'right' = 'left'
  ): void {
    if (!text) {
      return;
    }

    const baseFontSize = doc.getFontSize();
    const baseTwoWidth = doc.getTextWidth('2');
    const subscriptScale = 0.7;
    const subscriptYOffset = baseFontSize * 0.18;
    const segments = text.split('₂');
    const subscriptWidth = baseTwoWidth * subscriptScale;

    const totalWidth = segments.reduce((acc, segment, index) => {
      if (segment) {
        acc += doc.getTextWidth(segment);
      }
      if (index < segments.length - 1) {
        acc += subscriptWidth;
      }
      return acc;
    }, 0);

    let currentX = x;
    if (align === 'center') {
      currentX = x - totalWidth / 2;
    } else if (align === 'right') {
      currentX = x - totalWidth;
    }

    segments.forEach((segment, index) => {
      if (segment) {
        doc.text(segment, currentX, y);
        currentX += doc.getTextWidth(segment);
      }

      if (index < segments.length - 1) {
        doc.setFontSize(baseFontSize * subscriptScale);
        doc.text('2', currentX, y + subscriptYOffset);
        currentX += subscriptWidth;
        doc.setFontSize(baseFontSize);
      }
    });
  }

  /**
   * Encapsula doc.text aplicando subscritos quando necessário
   */
  private static drawText(
    doc: jsPDF,
    text: string,
    x: number,
    y: number,
    align: 'left' | 'center' | 'right' = 'left'
  ): void {
    if (!text) {
      return;
    }

    if (text.includes('₂')) {
      this.renderTextWithSubscripts(doc, text, x, y, align);
    } else if (align === 'left') {
      doc.text(text, x, y);
    } else {
      doc.text(text, x, y, { align });
    }
  }

  private static renderCPETBibliography(doc: jsPDF): void {
    const pageHeight = doc.internal.pageSize.getHeight();
    const lines = [
      'Referências:',
      'Wasserman K, Hansen JE, Sue DY, et al. Principles of Exercise Testing and Interpretation. 5th ed. Lippincott Williams & Wilkins; 2012.',
      'Fox SM III, Naughton JP, Haskell WL. Physical activity and the prevention of coronary heart disease. Ann N Y Acad Sci. 1973;301:593-610.'
    ];

    let y = pageHeight - 36;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);

    lines.forEach(line => {
      const wrapped = doc.splitTextToSize(line, doc.internal.pageSize.getWidth() - 30);
      wrapped.forEach((wrappedLine: string) => {
        doc.text(wrappedLine, 15, y);
        y += 3.5;
      });
    });
  }

  /**
   * Renderiza cartões com métricas principais
   */
  private static renderMetricCards(doc: jsPDF, startY: number, cards: MetricCard[]): number {
    if (!cards.length) {
      return startY;
    }

    const pageWidth = doc.internal.pageSize.getWidth();
    const usableWidth = pageWidth - 30;
    const cardsPerRow = 3;
    const cardSpacing = 6;
    const cardWidth = (usableWidth - cardSpacing * (cardsPerRow - 1)) / cardsPerRow;

    let currentY = startY;
    let currentRowHeight = 0;
    let currentX = 15;

    cards.forEach((card, index) => {
      const rowIndex = index % cardsPerRow;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const detailsArray = Array.isArray(card.details)
        ? card.details
        : card.details
        ? [card.details]
        : [];
      const detailLines = detailsArray.flatMap(detail =>
        doc.splitTextToSize(detail, cardWidth - 8)
      );
      const cardHeight = 24 + (detailLines.length ? detailLines.length * 4 : 0);

      if (rowIndex === 0) {
        if (index > 0) {
          currentY += currentRowHeight + cardSpacing;
        }
        currentX = 15;
        currentRowHeight = cardHeight;
      } else {
        currentX += cardWidth + cardSpacing;
        currentRowHeight = Math.max(currentRowHeight, cardHeight);
      }

      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(currentX, currentY, cardWidth, cardHeight, 3, 3, 'FD');

      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 116, 139);
      this.drawText(doc, card.label.toUpperCase(), currentX + 4, currentY + 6);

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      const [r, g, b] = card.accentColor || [37, 99, 235];
      doc.setTextColor(r, g, b);
      doc.text(card.value, currentX + 4, currentY + 16);

      if (detailLines.length) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(71, 85, 105);

        let detailY = currentY + 22;
        detailLines.forEach(line => {
          this.drawText(doc, line, currentX + 4, detailY);
          detailY += 4;
        });
      }
    });

    return currentY + currentRowHeight + cardSpacing;
  }

  /**
   * Renderiza bloco com duas colunas de informações
   */
  private static renderInfoColumns(
    doc: jsPDF,
    startY: number,
    leftItems: InfoColumnItem[],
    rightItems: InfoColumnItem[]
  ): number {
    if (!leftItems.length && !rightItems.length) {
      return startY;
    }

    const pageWidth = doc.internal.pageSize.getWidth();
    const columnWidth = (pageWidth - 40) / 2;
    const columnGap = 10;
    const leftX = 20;
    const rightX = leftX + columnWidth + columnGap;

    const mapItems = (items: InfoColumnItem[]) =>
      items.map(item => {
        const value = item.value || 'N/A';
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        const valueLines = doc.splitTextToSize(value, columnWidth - 6);
        return { ...item, valueLines };
      });

    const leftLayout = mapItems(leftItems);
    const rightLayout = mapItems(rightItems);

    const measureHeight = (layout: { valueLines: string[] }[]) =>
      layout.reduce((acc, entry) => acc + 4 + entry.valueLines.length * 4 + 2, 0);

    const boxHeight = Math.max(measureHeight(leftLayout), measureHeight(rightLayout)) + 12;

    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(15, startY, pageWidth - 30, boxHeight, 3, 3, 'FD');

    let leftCursor = startY + 8;
    let rightCursor = startY + 8;

    leftLayout.forEach(entry => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(100, 116, 139);
      this.drawText(doc, entry.label.toUpperCase(), leftX, leftCursor);
      leftCursor += 4;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(30, 41, 59);
      entry.valueLines.forEach((line: string) => {
        this.drawText(doc, line, leftX, leftCursor);
        leftCursor += 4;
      });

      leftCursor += 2;
    });

    rightLayout.forEach(entry => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(100, 116, 139);
      this.drawText(doc, entry.label.toUpperCase(), rightX, rightCursor);
      rightCursor += 4;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(30, 41, 59);
      entry.valueLines.forEach((line: string) => {
        this.drawText(doc, line, rightX, rightCursor);
        rightCursor += 4;
      });

      rightCursor += 2;
    });

    return startY + boxHeight + 10;
  }

  /**
 * Interpreta os dados do CPET e gera interpretação clínica
 */
private static interpretCPET(data: any): CPETInterpretation {
  const summary = data.summary;
  const vt1Sec = data.vt1Sec;
  const vt2Sec = data.vt2Sec;

  // Calcular percentuais preditos
  const age = data.patientAge || 50;
  const weight = data.patientWeight || 70;
  const height = data.patientHeight || 170;
  const isMale = data.patientSex === 'M';

  const predictedVO2_mlkgmin = isMale ? (60 - (0.55 * age)) : (48 - (0.37 * age));
  const predictedVO2_lmin = (predictedVO2_mlkgmin * weight) / 1000;
  const vo2PercentPredicted = (summary.vo2peak_lmin / predictedVO2_lmin) * 100;

  const predictedHRmax = 220 - age;
  const hrPercentPredicted = (summary.hr_peak / predictedHRmax) * 100;

  const predictedO2pulse = predictedVO2_lmin / predictedHRmax * 1000;
  const o2pulsePercentPredicted = (summary.o2pulse_peak_ml / predictedO2pulse) * 100;

  const summaryPredictedMVV = summary.predicted?.mvv ?? null;
  const measuredMVV = data.rows?.find((r: any) => r.MVV != null)?.MVV ?? null;
  const fev1FromData = data.rows?.find((r: any) => r.FEV1 != null)?.FEV1 ?? null;
  const estimatedMVV = summaryPredictedMVV ?? measuredMVV ?? (fev1FromData ? fev1FromData * 35 : 150);
  const vePeak = summary.ve_peak ?? (() => {
    const veValues = (data.rows || []).map((r: any) => r.VE).filter((v: any) => typeof v === 'number');
    return veValues.length ? Math.max(...veValues) : null;
  })();
  const veMvvPercent = vePeak != null && estimatedMVV ? (vePeak / estimatedMVV) * 100 : null;
  const ventReserve = vePeak != null && estimatedMVV
    ? ((estimatedMVV - vePeak) / estimatedMVV) * 100
    : null;

  const vt1PercentVO2 = vt1Sec ? ((data.rows.find((r: any) => Math.abs(r.tSec - vt1Sec) < 1)?.VO2_l_min || 0) / summary.vo2peak_lmin) * 100 : 0;
  const vt2PercentVO2 = vt2Sec ? ((data.rows.find((r: any) => Math.abs(r.tSec - vt2Sec) < 1)?.VO2_l_min || 0) / summary.vo2peak_lmin) * 100 : 0;
  const veAtVT1 = summary.ve_vt1 ?? (vt1Sec ? data.rows.find((r: any) => Math.abs(r.tSec - vt1Sec) < 1)?.VE ?? null : null);
  const veAtVT1Percent = veAtVT1 != null && vePeak != null ? (veAtVT1 / vePeak) * 100 : null;

  const rerPeak = summary.rer_peak ?? (() => {
    const rerValues = (data.rows || []).map((r: any) => r.RER).filter((v: any) => typeof v === 'number');
    return rerValues.length ? Math.max(...rerValues) : null;
  })();

  const spo2Rest = summary.spo2_rest ?? (() => {
    const values = (data.rows || []).map((r: any) => r.SpO2).filter((v: any) => typeof v === 'number');
    return values.length ? values[0] : null;
  })();
  const spo2Peak = summary.spo2_peak ?? (() => {
    const values = (data.rows || []).map((r: any) => r.SpO2).filter((v: any) => typeof v === 'number');
    return values.length ? Math.min(...values) : null;
  })();
  const spo2Drop = summary.spo2_drop ?? (spo2Rest != null && spo2Peak != null ? spo2Rest - spo2Peak : null);
  const vo2PlateauFlag = typeof summary.vo2_plateau === 'boolean'
    ? summary.vo2_plateau
    : null;

  // Classificação da capacidade funcional
  let functionalClassification: CPETInterpretation['functionalCapacity']['classification'];
  let functionalDescription: string;

  if (vo2PercentPredicted >= 85) {
    functionalClassification = 'Normal';
    functionalDescription = `A capacidade aeróbica está preservada, com VO₂ pico de ${summary.vo2peak_lmin.toFixed(2)} L/min (${summary.vo2peak_mlkg.toFixed(1)} mL/kg/min), correspondendo a ${vo2PercentPredicted.toFixed(0)}% do valor predito para idade, sexo e peso. Este resultado indica adequada capacidade funcional para atividades de vida diária e exercício físico.`;
  } else if (vo2PercentPredicted >= 70) {
    functionalClassification = 'Levemente reduzida';
    functionalDescription = `A capacidade aeróbica está levemente reduzida, com VO₂ pico de ${summary.vo2peak_lmin.toFixed(2)} L/min (${summary.vo2peak_mlkg.toFixed(1)} mL/kg/min), correspondendo a ${vo2PercentPredicted.toFixed(0)}% do valor predito. Esta redução pode estar relacionada a descondicionamento físico ou estágios iniciais de comprometimento cardiopulmonar.`;
  } else if (vo2PercentPredicted >= 50) {
    functionalClassification = 'Moderadamente reduzida';
    functionalDescription = `A capacidade aeróbica está moderadamente reduzida, com VO₂ pico de ${summary.vo2peak_lmin.toFixed(2)} L/min (${summary.vo2peak_mlkg.toFixed(1)} mL/kg/min), correspondendo a ${vo2PercentPredicted.toFixed(0)}% do valor predito. Esta redução indica comprometimento funcional significativo que pode limitar atividades de vida diária e exercício físico.`;
  } else {
    functionalClassification = 'Gravemente reduzida';
    functionalDescription = `A capacidade aeróbica está gravemente reduzida, com VO₂ pico de ${summary.vo2peak_lmin.toFixed(2)} L/min (${summary.vo2peak_mlkg.toFixed(1)} mL/kg/min), correspondendo a ${vo2PercentPredicted.toFixed(0)}% do valor predito. Esta redução grave indica comprometimento funcional importante que limita substancialmente atividades de vida diária.`;
  }

  // Análise de limitações - CARDIOVASCULAR
  const cardioEvidence: string[] = [];
  let cardioPresent = false;
  let cardioSeverity: CPETInterpretation['limitations']['cardiovascular']['severity'] = 'Ausente';

  if (hrPercentPredicted < 85) {
    cardioEvidence.push(`Resposta cronotrópica inadequada (FC pico ${summary.hr_peak} bpm = ${hrPercentPredicted.toFixed(0)}% predito)`);
    cardioPresent = true;
  }
  if (o2pulsePercentPredicted < 80) {
    cardioEvidence.push(`Pulso de O₂ reduzido (${summary.o2pulse_peak_ml.toFixed(1)} mL/bat = ${o2pulsePercentPredicted.toFixed(0)}% predito), sugerindo limitação de volume sistólico`);
    cardioPresent = true;
  }
  if (vt1PercentVO2 < 40 && vt1PercentVO2 > 0) {
    cardioEvidence.push(`Limiar anaeróbio precoce (${vt1PercentVO2.toFixed(0)}% VO₂ pico), indicando possível limitação de oferta de oxigênio`);
    cardioPresent = true;
  }
  if (summary.vevco2_slope > 34) {
    cardioEvidence.push(`VE/VCO₂ slope elevado (${summary.vevco2_slope.toFixed(1)}), sugerindo ineficiência ventilatória compatível com insuficiência cardíaca ou hipertensão pulmonar`);
    cardioPresent = true;
  }

  if (cardioPresent) {
    if (cardioEvidence.length >= 3) {
      cardioSeverity = 'Grave';
    } else if (cardioEvidence.length === 2) {
      cardioSeverity = 'Moderada';
    } else {
      cardioSeverity = 'Leve';
    }
  }

  // VENTILATÓRIA
  const ventEvidence: string[] = [];
  let ventPresent = false;
  let ventSeverity: CPETInterpretation['limitations']['ventilatory']['severity'] = 'Ausente';
  if (ventReserve != null && ventReserve < 15) {
    ventEvidence.push(`Reserva ventilatória reduzida (${ventReserve.toFixed(0)}%), indicando limitação ventilatória mecânica`);
    ventPresent = true;
  }
  if (summary.vevco2_slope > 30 && summary.vevco2_slope <= 34) {
    ventEvidence.push(`VE/VCO₂ slope limítrofe (${summary.vevco2_slope.toFixed(1)}), sugerindo leve ineficiência ventilatória`);
    ventPresent = true;
  }
  if (veAtVT1Percent != null && veAtVT1Percent >= 60) {
    ventEvidence.push(`VE @ VT1 elevado (${veAtVT1Percent.toFixed(0)}% do VE pico)`);
    ventPresent = true;
  }

  if (ventPresent) {
    if (ventReserve != null && ventReserve < 10) ventSeverity = 'Grave';
    else if (ventEvidence.length >= 2 || (ventReserve != null && ventReserve < 15)) ventSeverity = 'Moderada';
    else ventSeverity = 'Leve';
  }

  // PERIFÉRICA/MUSCULAR
  const peripheralEvidence: string[] = [];
  let peripheralPresent = false;
  let peripheralSeverity: CPETInterpretation['limitations']['peripheral']['severity'] = 'Ausente';

  if (vo2PercentPredicted < 85 && !cardioPresent && !ventPresent && (data.series?.[data.series.length - 1]?.RER || 1.0) > 1.10) {
    peripheralEvidence.push(`VO₂ pico reduzido com respostas cardiovascular e ventilatória preservadas, sugerindo limitação periférica/muscular`);
    peripheralPresent = true;
  }
  if (vt1PercentVO2 < 40 && vt1PercentVO2 > 0 && summary.vevco2_slope <= 30) {
    peripheralEvidence.push(`Limiar anaeróbio precoce com eficiência ventilatória normal, compatível com limitação muscular`);
    peripheralPresent = true;
  }

  if (peripheralPresent) {
    if (vo2PercentPredicted < 50) peripheralSeverity = 'Grave';
    else if (vo2PercentPredicted < 70) peripheralSeverity = 'Moderada';
    else peripheralSeverity = 'Leve';
  }

  // DESCONDICIONAMENTO
  const deconditioningEvidence: string[] = [];
  let deconditioningPresent = false;

  if (vo2PercentPredicted >= 70 && vo2PercentPredicted < 85 && !cardioPresent && !ventPresent && (data.series?.[data.series.length - 1]?.RER || 1.0) > 1.10) {
    deconditioningEvidence.push(`Capacidade aeróbica levemente reduzida com esforço máximo adequado (RER ${(data.series?.[data.series.length - 1]?.RER || 1.0).toFixed(2)})`);
    deconditioningEvidence.push(`Ausência de evidências de limitação cardiopulmonar significativa`);
    deconditioningPresent = true;
  }

  // Limitação primária
  let primaryLimitation = 'Indeterminada';
  
  if ((cardioSeverity as CPETInterpretation['limitations']['cardiovascular']['severity']) === 'Grave' || ((cardioSeverity as CPETInterpretation['limitations']['cardiovascular']['severity']) === 'Moderada' && (ventSeverity as CPETInterpretation['limitations']['ventilatory']['severity']) !== 'Grave')) {    primaryLimitation = 'Cardiovascular';
  } else if ((ventSeverity as CPETInterpretation['limitations']['ventilatory']['severity']) === 'Grave' || ((ventSeverity as CPETInterpretation['limitations']['ventilatory']['severity']) === 'Moderada' && (cardioSeverity as CPETInterpretation['limitations']['cardiovascular']['severity']) !== 'Grave')) {    primaryLimitation = 'Ventilatória/Pulmonar';
  } else if (peripheralSeverity === 'Grave' || peripheralSeverity === 'Moderada') {
    primaryLimitation = 'Periférica/Muscular';
  } else if (deconditioningPresent) {
    primaryLimitation = 'Descondicionamento físico';
  } else if (cardioPresent && ventPresent) {
    primaryLimitation = 'Mista (Cardiovascular e Ventilatória)';
  } else if (vo2PercentPredicted >= 85) {
    primaryLimitation = 'Sem limitação significativa identificada';
  }

  const normalizeCriterion = (text: string): string =>
    text.replace(/≥/g, '>=').replace(/≤/g, '<=');

  const maximalCriteria: string[] = [];
  if (vo2PlateauFlag === true) {
    maximalCriteria.push('Plateau de VO₂ nos estágios finais (<0,15 L/min)');
  }
  if (!Number.isNaN(hrPercentPredicted) && hrPercentPredicted >= 90) {
    maximalCriteria.push(`FC pico >= 90% predita (${hrPercentPredicted.toFixed(0)}%)`);
  }
  if (veMvvPercent != null && veMvvPercent >= 80) {
    maximalCriteria.push(`VE pico >= 80% MVV (${veMvvPercent.toFixed(0)}%)`);
  }
  if (rerPeak != null && rerPeak >= 1.05) {
    maximalCriteria.push(`RER pico >= 1,05 (${rerPeak.toFixed(2)})`);
  }
  const maximalCriteriaNormalized = maximalCriteria.map(normalizeCriterion);
  const maximalDataAvailable = [
    vo2PlateauFlag !== null,
    !Number.isNaN(hrPercentPredicted),
    veMvvPercent != null,
    rerPeak != null
  ].some(Boolean);
  const isMaximalTest = maximalCriteria.length > 0 ? true : maximalDataAvailable ? false : null;

  const abnormalCriteria: string[] = [];
  if (vo2PercentPredicted < 80) {
    abnormalCriteria.push(`VO₂ pico ${vo2PercentPredicted.toFixed(0)}% do predito (<80%)`);
  }
  if (!Number.isNaN(hrPercentPredicted) && hrPercentPredicted < 80) {
    abnormalCriteria.push(`FC pico ${hrPercentPredicted.toFixed(0)}% da predita (<80%)`);
  }
  if (veMvvPercent != null && veMvvPercent >= 80) {
    abnormalCriteria.push(`VE pico ${veMvvPercent.toFixed(0)}% da MVV (≥80%)`);
  }
  if (vt1PercentVO2 > 0 && vt1PercentVO2 < 60) {
    abnormalCriteria.push(`VO₂ no VT1 em ${vt1PercentVO2.toFixed(0)}% do VO₂ pico (<60%)`);
  }
  if (veAtVT1Percent != null && veAtVT1Percent >= 60) {
    abnormalCriteria.push(`VE @ VT1 em ${veAtVT1Percent.toFixed(0)}% do VE pico (≥60%)`);
  }
  if (spo2Drop != null && spo2Drop >= 5) {
    abnormalCriteria.push(`Dessaturação ≥5% (queda de ${spo2Drop.toFixed(1)}%)`);
  }
  const abnormalCriteriaNormalized = abnormalCriteria.map(normalizeCriterion);
  const abnormalDataAvailable = [
    true, // sempre temos vo2PercentPredicted
    !Number.isNaN(hrPercentPredicted),
    veMvvPercent != null,
    vt1PercentVO2 > 0,
    spo2Drop != null
  ].some(Boolean);
  const abnormalPresent = abnormalCriteria.length > 0 ? true : abnormalDataAvailable ? false : null;

  // Zonas de treinamento
  const vt1HR = vt1Sec ? data.rows.find((r: any) => Math.abs(r.tSec - vt1Sec) < 1)?.FC || 0 : 0;
  const vt2HR = vt2Sec ? data.rows.find((r: any) => Math.abs(r.tSec - vt2Sec) < 1)?.FC || 0 : 0;

  const trainingZones = {
    zone1: {
      name: 'Zona 1 - Recuperação',
      hrRange: vt1HR > 0 ? `< ${vt1HR - 10} bpm` : `< ${Math.round(predictedHRmax * 0.6)} bpm`,
      description: 'Recuperação ativa, aquecimento e desaquecimento'
    },
    zone2: {
      name: 'Zona 2 - Aeróbico Leve',
      hrRange: vt1HR > 0 ? `${vt1HR - 10} - ${vt1HR + 10} bpm` : `${Math.round(predictedHRmax * 0.6)} - ${Math.round(predictedHRmax * 0.7)} bpm`,
      description: 'Base aeróbica, oxidação de gordura, maior volume de treino'
    },
    zone3: {
      name: 'Zona 3 - Aeróbico Moderado',
      hrRange: vt1HR > 0 && vt2HR > 0 ? `${vt1HR + 10} - ${vt2HR - 10} bpm` : `${Math.round(predictedHRmax * 0.7)} - ${Math.round(predictedHRmax * 0.8)} bpm`,
      description: 'Limiar aeróbico, melhora de VO₂ máx, treinos moderados'
    },
    zone4: {
      name: 'Zona 4 - Limiar Anaeróbio',
      hrRange: vt2HR > 0 ? `${vt2HR - 10} - ${vt2HR + 10} bpm` : `${Math.round(predictedHRmax * 0.8)} - ${Math.round(predictedHRmax * 0.9)} bpm`,
      description: 'Tolerância ao lactato, intervalados moderados'
    },
    zone5: {
      name: 'Zona 5 - Alta Intensidade',
      hrRange: vt2HR > 0 ? `> ${vt2HR + 10} bpm` : `> ${Math.round(predictedHRmax * 0.9)} bpm`,
      description: 'VO₂ máx, intervalados curtos e intensos'
    }
  };

  // Recomendações
  const recommendations: string[] = [];

  if (primaryLimitation === 'Cardiovascular') {
    recommendations.push('Reabilitação cardíaca supervisionada');
    recommendations.push(`Treinamento aeróbico em Zona 2 (${trainingZones.zone2.hrRange})`);
    recommendations.push('Avaliação cardiológica complementar para otimização terapêutica');
    recommendations.push('Evitar exercícios de alta intensidade sem supervisão');
  } else if (primaryLimitation === 'Ventilatória/Pulmonar') {
    recommendations.push('Reabilitação pulmonar supervisionada');
    recommendations.push(`Treinamento aeróbico em Zona 2-3 (${trainingZones.zone2.hrRange})`);
    recommendations.push('Considerar treinamento muscular inspiratório');
    recommendations.push('Avaliação pneumológica para otimização da terapia broncodilatadora');
  } else if (primaryLimitation === 'Periférica/Muscular') {
    recommendations.push('Treinamento de força e resistência muscular');
    recommendations.push(`Treinamento aeróbico progressivo em Zona 2-3 (${trainingZones.zone2.hrRange})`);
    recommendations.push('Avaliação nutricional');
    recommendations.push('Considerar avaliação neurológica se suspeita de miopatia');
  } else if (primaryLimitation === 'Descondicionamento físico') {
    recommendations.push('Programa de condicionamento físico progressivo');
    recommendations.push(`Treinamento aeróbico em Zona 2-3 (${trainingZones.zone2.hrRange}), 3-5x/semana`);
    recommendations.push('Incorporar treinamento de força 2-3x/semana');
    recommendations.push('Progressão gradual de intensidade e volume');
  } else {
    recommendations.push('Manutenção de atividade física regular');
    recommendations.push(`Treinamento aeróbico variado em Zonas 2-4 (${trainingZones.zone2.hrRange} a ${trainingZones.zone4.hrRange})`);
    recommendations.push('Incorporar treinamento intervalado para melhora de performance');
  }

  recommendations.push(`Reavaliação em 3-6 meses para monitorar evolução`);

  // Resumo clínico
  let clinicalSummary = `Teste cardiopulmonar de esforço realizado em protocolo de rampa incremental. `;
  clinicalSummary += functionalDescription + ' ';
  
  if (primaryLimitation !== 'Sem limitação significativa identificada') {
    clinicalSummary += `A limitação primária ao exercício é de origem ${primaryLimitation.toLowerCase()}. `;
  }

  if (vt1PercentVO2 > 0) {
    clinicalSummary += `O limiar anaeróbio (VT1) foi identificado em ${vt1PercentVO2.toFixed(0)}% do VO₂ pico. `;
  }

  if (vt2PercentVO2 > 0) {
    clinicalSummary += `O ponto de compensação respiratória (VT2) foi identificado em ${vt2PercentVO2.toFixed(0)}% do VO₂ pico. `;
  }

  clinicalSummary += `A eficiência ventilatória (VE/VCO₂ slope = ${summary.vevco2_slope.toFixed(1)}) está ${summary.vevco2_slope > 30 ? 'elevada' : 'normal'}. `;
  clinicalSummary += `O teste foi considerado máximo com RER pico de ${(data.series?.[data.series.length - 1]?.RER || 1.0).toFixed(2)}. `;

  return {
    functionalCapacity: {
      classification: functionalClassification,
      percentPredicted: vo2PercentPredicted,
      description: functionalDescription
    },
    limitations: {
      cardiovascular: {
        present: cardioPresent,
        severity: cardioSeverity,
        evidence: cardioEvidence
      },
      ventilatory: {
        present: ventPresent,
        severity: ventSeverity,
        evidence: ventEvidence
      },
      peripheral: {
        present: peripheralPresent,
        severity: peripheralSeverity,
        evidence: peripheralEvidence
      },
      deconditioning: {
        present: deconditioningPresent,
        evidence: deconditioningEvidence
      }
    },
    primaryLimitation,
    trainingZones,
    recommendations,
    clinicalSummary,
    maximalTest: {
      isMaximal: isMaximalTest,
      criteriaMet: maximalCriteriaNormalized
    },
    abnormalResponse: {
      present: abnormalPresent,
      criteriaMet: abnormalCriteriaNormalized
    },
    terminationNotes: data.summary?.terminationNotes ?? data.terminationNotes ?? null
  };
}
  
  /**
   * Renderiza o rodapé padrão
   */
  private static renderFooter(doc: jsPDF, pageNumber: number, totalPages: number): void {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(15, pageHeight - 25, pageWidth - 15, pageHeight - 25);

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(reportSystemLine, pageWidth / 2, pageHeight - 18, { align: 'center' });
    doc.text(`Data de emissão: ${new Date().toLocaleString('pt-BR')} | Documento confidencial - uso restrito`, pageWidth / 2, pageHeight - 14, { align: 'center' });
    doc.text(clinicLegalLine, pageWidth / 2, pageHeight - 10, { align: 'center' });
    doc.text(`Página ${pageNumber} de ${totalPages}`, pageWidth - 15, pageHeight - 10, { align: 'right' });
  }

  /**
   * Renderiza informações básicas do paciente e da avaliação
   */
  private static renderPatientInfo(doc: jsPDF, assessment: Assessment, yPosition: number): number {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Dados do Paciente:', 15, yPosition);
    yPosition += 7;

    doc.setFont('helvetica', 'normal');
    doc.text(`Nome: ${assessment.patientName}`, 15, yPosition);
    yPosition += 6;

    if (assessment.patientProntuario) {
      doc.text(`Prontuário: ${assessment.patientProntuario}`, 15, yPosition);
      yPosition += 6;
    }

    if (assessment.patientBirthDate) {
      doc.text(`Data de Nascimento: ${assessment.patientBirthDate}`, 15, yPosition);
      yPosition += 6;
    }

    yPosition += 3;

    doc.setFont('helvetica', 'bold');
    doc.text('Dados da Avaliação:', 15, yPosition);
    yPosition += 7;

    doc.setFont('helvetica', 'normal');
    doc.text(`Data da Avaliação: ${this.formatDate(assessment.datetime || assessment.createdAt)}`, 15, yPosition);
    yPosition += 6;

    if (assessment.doctorName) {
      let doctorText = `Profissional: ${assessment.doctorName}`;
      if (assessment.doctorRegistration) {
        doctorText += ` - ${assessment.doctorRegistration}`;
      }
      doc.text(doctorText, 15, yPosition);
      yPosition += 6;
    }

    return yPosition + 5;
  }

  /**
   * Desenha um gráfico de linha aprimorado com cores pastel, efeito 3D e suporte a eixos Y duplos
   */
  private static drawLineChart(
    doc: jsPDF,
    x: number,
    y: number,
    width: number,
    height: number,
    data: { label: string; values: number[]; color: [number, number, number]; unit: string; yMin?: number; yMax?: number; }[],
    xLabels: string[],
    title: string,
    yAxisLabel: string,
    yMin?: number,
    yMax?: number,
    horizontalLines?: { value: number; color: [number, number, number]; label: string; dash?: boolean }[]
  ): void {
    // Converter cores para pastel
    const toPastel = (rgb: [number, number, number]): [number, number, number] => [
      Math.min(255, rgb[0] + (255 - rgb[0]) * 0.35),
      Math.min(255, rgb[1] + (255 - rgb[1]) * 0.35),
      Math.min(255, rgb[2] + (255 - rgb[2]) * 0.35)
    ];

    // Detectar se há eixos Y duplos (múltiplas séries com escalas diferentes)
    const hasDualYAxes = data.length > 1 && (data[0].yMin !== undefined || data[0].yMax !== undefined);

    // Título do gráfico
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 60, 70);
    doc.text(title, x + width / 2, y - 5, { align: 'center' });

    // Legenda (logo abaixo do título)
    if (data.length > 1) {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      let legendX = x + width / 2 - (data.length * 25);
      data.forEach((series) => {
        const pastelColor = toPastel(series.color);
        // Desenhar pequeno retângulo colorido
        doc.setFillColor(pastelColor[0], pastelColor[1], pastelColor[2]);
        doc.rect(legendX, y + 2, 8, 2, 'F');
        // Texto da legenda
        doc.setTextColor(80, 80, 90);

        if (series.label === 'SpO2' || series.label === 'SpO₂') {
          this.renderSpO2Text(doc, legendX + 10, y + 4, 'left');
          doc.text(` (${series.unit})`, legendX + 10 + doc.getTextWidth('SpO') + 3, y + 4);
        } else {
          doc.text(`${series.label} (${series.unit})`, legendX + 10, y + 4);
        }
        legendX += 50;
      });
    }

    // Sombra do fundo
    doc.setFillColor(230, 230, 235);
    doc.rect(x + 1, y + 1, width, height, 'F');

    // Fundo do gráfico
    doc.setFillColor(255, 255, 255);
    doc.rect(x, y, width, height, 'F');

    // Bordas suaves
    doc.setDrawColor(200, 200, 210);
    doc.setLineWidth(0.3);
    doc.rect(x, y, width, height);

    // Calcular min/max dos dados se não fornecidos
    // Para eixos duplos, cada série tem sua própria escala
    const xStep = width / (xLabels.length - 1);

    // Configurar escalas para cada série
    const seriesScales = data.map((series, idx) => {
      let seriesYMin = series.yMin ?? yMin;
      let seriesYMax = series.yMax ?? yMax;

      if (seriesYMin === undefined || seriesYMax === undefined) {
        const values = series.values.filter(v => v !== null && !isNaN(v));
        if (values.length > 0) {
          seriesYMin = seriesYMin ?? Math.floor(Math.min(...values) * 0.9);
          seriesYMax = seriesYMax ?? Math.ceil(Math.max(...values) * 1.1);
        } else {
          seriesYMin = 0;
          seriesYMax = 100;
        }
      }

      return {
        yMin: seriesYMin,
        yMax: seriesYMax,
        yRange: seriesYMax - seriesYMin,
        isRightAxis: hasDualYAxes && idx === 0 // FC no eixo direito
      };
    });

    // Usar a primeira série para referências gerais (ou escala combinada se não houver eixos duplos)
    const primaryScale = seriesScales[0];
    const yRange = primaryScale.yRange;

    // Grade horizontal suave
    doc.setDrawColor(240, 240, 245);
    doc.setLineWidth(0.2);
    for (let i = 0; i <= 5; i++) {
      const gridY = y + (height / 5) * i;
      doc.line(x, gridY, x + width, gridY);
    }

    // Linhas horizontais de referência
    if (horizontalLines && horizontalLines.length > 0) {
      horizontalLines.forEach(line => {
        // Para eixos duplos, usar a escala do SpO2 (segunda série)
        // Para eixo único, usar a escala primária
        const referenceScale = hasDualYAxes && data.length > 1 ? seriesScales[1] : primaryScale;
        const effectiveYMin = referenceScale.yMin;
        const effectiveYRange = referenceScale.yRange;

        const lineY = y + height - ((line.value - effectiveYMin) / effectiveYRange) * height;

        // Validar se a linha está dentro dos limites do gráfico
        if (isNaN(lineY) || !isFinite(lineY) || lineY < y || lineY > y + height) {
          console.warn(`Skipping horizontal line "${line.label}" due to invalid or out-of-bounds coordinates`);
          return;
        }

        const pastelColor = toPastel(line.color);
        doc.setDrawColor(pastelColor[0], pastelColor[1], pastelColor[2]);
        doc.setLineWidth(0.8);
        if (line.dash) {
          // Implementação alternativa ou fallback para setLineDash
          // Dependendo da versão do jspdf, pode ser necessário um plugin
          // ou verificar a existência do método e, se não existir, ignorar
          // Por enquanto, vamos manter a linha sólida se o método não existe
          // Caso contrário, use a asserção de tipo para jspdf-yolo (se disponível)
          if (typeof (doc as any).setLineDash === 'function') {
            (doc as any).setLineDash([3, 2]);
          } else {
            console.warn("setLineDash não disponível na instância jsPDF. Linha renderizada como sólida.");
          }
        }
        doc.line(x, lineY, x + width, lineY);
        if (typeof (doc as any).setLineDash === 'function') {
          (doc as any).setLineDash([]); // Resetar para linha sólida
        }

        doc.setFontSize(7);
        doc.setTextColor(pastelColor[0] * 0.7, pastelColor[1] * 0.7, pastelColor[2] * 0.7);
        doc.text(line.label, x + width + 2, lineY + 2);
      });
    }

    // Grade vertical suave
    doc.setDrawColor(245, 245, 250);
    doc.setLineWidth(0.2);
    xLabels.forEach((_, i) => {
      const lineX = x + i * xStep;
      doc.line(lineX, y, lineX, y + height);
    });

    // Labels do eixo X
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 90);
    xLabels.forEach((label, i) => {
      const labelX = x + i * xStep;
      doc.text(label, labelX, y + height + 5, { align: 'center' });
    });

    // Labels e valores dos eixos Y
    doc.setFontSize(8);

    if (hasDualYAxes) {
      // Eixo Y esquerdo (SpO2 - azul)
      const spo2Scale = seriesScales[1]; // SpO2 é a segunda série
      const spo2Color = data[1].color;
      doc.setTextColor(spo2Color[0], spo2Color[1], spo2Color[2]);
      doc.text(`${data[1].label} (${data[1].unit})`, x - 8, y + height / 2, { align: 'center', angle: 90 });

      // Valores do eixo Y esquerdo
      doc.setFontSize(7);
      for (let i = 0; i <= 5; i++) {
        const value = spo2Scale.yMin + (spo2Scale.yRange / 5) * (5 - i);
        const yValPos = y + (height / 5) * i;
        doc.text(value.toFixed(0), x - 2, yValPos + 2, { align: 'right' });
      }

      // Eixo Y direito (FC - vermelho)
      const fcScale = seriesScales[0]; // FC é a primeira série
      const fcColor = data[0].color;
      doc.setTextColor(fcColor[0], fcColor[1], fcColor[2]);
      doc.text(`${data[0].label} (${data[0].unit})`, x + width + 8, y + height / 2, { align: 'center', angle: 90 });

      // Valores do eixo Y direito
      doc.setFontSize(7);
      for (let i = 0; i <= 5; i++) {
        const value = fcScale.yMin + (fcScale.yRange / 5) * (5 - i);
        const yValPos = y + (height / 5) * i;
        doc.text(value.toFixed(0), x + width + 2, yValPos + 2, { align: 'left' });
      }
    } else {
      // Eixo Y único (comportamento original)
      doc.setTextColor(80, 80, 90);
      doc.text(yAxisLabel, x - 5, y + height / 2, { align: 'center', angle: 90 });
    }

    // Desenhar as linhas de dados com efeito de profundidade
    data.forEach((series, seriesIdx) => {
      const pastelColor = toPastel(series.color);
      const scale = seriesScales[seriesIdx];

      // Sombra da linha
      doc.setDrawColor(pastelColor[0] * 0.7, pastelColor[1] * 0.7, pastelColor[2] * 0.7);
      doc.setLineWidth(2.5);

      for (let i = 0; i < series.values.length - 1; i++) {
        const val1 = series.values[i];
        const val2 = series.values[i + 1];

        if (val1 !== null && val2 !== null && !isNaN(val1) && !isNaN(val2)) {
          const x1 = x + i * xStep;
          const y1 = y + height - ((val1 - scale.yMin) / scale.yRange) * height;
          const x2 = x + (i + 1) * xStep;
          const y2 = y + height - ((val2 - scale.yMin) / scale.yRange) * height;

          doc.line(x1 + 0.3, y1 + 0.3, x2 + 0.3, y2 + 0.3);
        }
      }

      // Linha principal
      doc.setDrawColor(pastelColor[0], pastelColor[1], pastelColor[2]);
      doc.setLineWidth(2);

      for (let i = 0; i < series.values.length - 1; i++) {
        const val1 = series.values[i];
        const val2 = series.values[i + 1];

        if (val1 !== null && val2 !== null && !isNaN(val1) && !isNaN(val2)) {
          const x1 = x + i * xStep;
          const y1 = y + height - ((val1 - scale.yMin) / scale.yRange) * height;
          const x2 = x + (i + 1) * xStep;
          const y2 = y + height - ((val2 - scale.yMin) / scale.yRange) * height;

          doc.line(x1, y1, x2, y2);

          // Pontos com efeito 3D
          doc.setFillColor(pastelColor[0] * 0.7, pastelColor[1] * 0.7, pastelColor[2] * 0.7);
          doc.circle(x1 + 0.3, y1 + 0.3, 2, 'F');

          doc.setFillColor(pastelColor[0], pastelColor[1], pastelColor[2]);
          doc.circle(x1, y1, 2, 'F');

          doc.setFillColor(255, 255, 255);
          doc.circle(x1 - 0.3, y1 - 0.3, 0.5, 'F');

          if (i === series.values.length - 2) {
            doc.setFillColor(pastelColor[0] * 0.7, pastelColor[1] * 0.7, pastelColor[2] * 0.7);
            doc.circle(x2 + 0.3, y2 + 0.3, 2, 'F');

            doc.setFillColor(pastelColor[0], pastelColor[1], pastelColor[2]);
            doc.circle(x2, y2, 2, 'F');

            doc.setFillColor(255, 255, 255);
            doc.circle(x2 - 0.3, y2 - 0.3, 0.5, 'F');
          }
        }
      }
    });

    // Legenda moderna
    let legendY = y + height + 12;
    doc.setFontSize(8);
    data.forEach((series, idx) => {
      const legendX = x + idx * 60;
      const pastelColor = toPastel(series.color);

      // Sombra do retângulo
      doc.setFillColor(220, 220, 230);
      doc.roundedRect(legendX + 0.5, legendY - 1.5, 8, 3, 0.5, 0.5, 'F');

      // Retângulo de cor
      doc.setFillColor(pastelColor[0], pastelColor[1], pastelColor[2]);
      doc.setDrawColor(pastelColor[0] * 0.8, pastelColor[1] * 0.8, pastelColor[2] * 0.8);
      doc.setLineWidth(0.2);
      doc.roundedRect(legendX, legendY - 2, 8, 3, 0.5, 0.5, 'FD');

      doc.setTextColor(60, 60, 70);
      if (series.label === 'SpO2' || series.label === 'SpO₂') {
        this.renderSpO2Text(doc, legendX + 10, legendY, 'left');
        doc.text(` (${series.unit})`, legendX + 10 + doc.getTextWidth('SpO') + 3, legendY);
      } else {
        doc.text(`${series.label} (${series.unit})`, legendX + 10, legendY);
      }
    });
  }

  /**
   * Desenha um gráfico de barras aprimorado com efeito 3D e cores pastel
   */
  private static drawBarChart(
    doc: jsPDF,
    x: number,
    y: number,
    width: number,
    height: number,
    data: { label: string; value: number; color?: [number, number, number] }[],
    title: string,
    yAxisLabel: string,
    yMax?: number
  ): void {
    // Converter cores para pastel
    const toPastel = (rgb: [number, number, number]): [number, number, number] => [
      Math.min(255, rgb[0] + (255 - rgb[0]) * 0.35),
      Math.min(255, rgb[1] + (255 - rgb[1]) * 0.35),
      Math.min(255, rgb[2] + (255 - rgb[2]) * 0.35)
    ];

    // Título do gráfico
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 60, 70);
    doc.text(title, x + width / 2, y - 5, { align: 'center' });

    // Sombra do fundo
    doc.setFillColor(230, 230, 235);
    doc.rect(x + 1, y + 1, width, height, 'F');

    // Fundo do gráfico
    doc.setFillColor(255, 255, 255);
    doc.rect(x, y, width, height, 'F');

    // Bordas suaves
    doc.setDrawColor(200, 200, 210);
    doc.setLineWidth(0.3);
    doc.rect(x, y, width, height);

    // Calcular yMax se não fornecido
    if (!yMax) {
      yMax = Math.ceil(Math.max(...data.map(d => d.value)) * 1.1);
    }

    // Grade horizontal suave
    doc.setDrawColor(240, 240, 245);
    doc.setLineWidth(0.2);
    for (let i = 0; i <= 5; i++) {
      const gridY = y + (height / 5) * i;
      doc.line(x, gridY, x + width, gridY);
    }

    const barWidth = width / data.length * 0.7;
    const barSpacing = width / data.length;

    // Desenhar barras com efeito 3D
    data.forEach((item, i) => {
      const barHeight = (item.value / yMax!) * height;
      const barX = x + i * barSpacing + (barSpacing - barWidth) / 2;
      const barY = y + height - barHeight;

      const color = item.color || [0, 102, 204];
      const pastelColor = toPastel(color);

      // Sombra da barra (lateral direita)
      doc.setFillColor(pastelColor[0] * 0.6, pastelColor[1] * 0.6, pastelColor[2] * 0.6);
      doc.rect(barX + barWidth, barY + 1, 2, barHeight, 'F');

      // Sombra da barra (base)
      doc.setFillColor(pastelColor[0] * 0.5, pastelColor[1] * 0.5, pastelColor[2] * 0.5);
      doc.rect(barX + 2, y + height, barWidth, 2, 'F');

      // Barra principal com gradiente simulado
      const gradientSteps = 8;
      for (let j = 0; j < gradientSteps; j++) {
        const gradientFactor = 1 - (j / gradientSteps) * 0.15;
        const segmentHeight = barHeight / gradientSteps;
        const segmentY = barY + j * segmentHeight;

        doc.setFillColor(
          pastelColor[0] * gradientFactor,
          pastelColor[1] * gradientFactor,
          pastelColor[2] * gradientFactor
        );
        doc.rect(barX, segmentY, barWidth, segmentHeight, 'F');
      }

      // Borda da barra
      doc.setDrawColor(pastelColor[0] * 0.8, pastelColor[1] * 0.8, pastelColor[2] * 0.8);
      doc.setLineWidth(0.3);
      doc.rect(barX, barY, barWidth, barHeight);

      // Brilho superior
      doc.setFillColor(255, 255, 255);
      const highlightHeight = Math.min(barHeight * 0.15, 3);
      doc.rect(barX + 1, barY + 1, barWidth - 2, highlightHeight, 'F');

      // Caixa de valor acima da barra
      const valueBoxWidth = Math.min(barWidth * 0.8, 20);
      const valueBoxHeight = 6;
      const valueBoxX = barX + (barWidth - valueBoxWidth) / 2;
      const valueBoxY = barY - valueBoxHeight - 2;

      // Sombra da caixa
      doc.setFillColor(220, 220, 230);
      doc.roundedRect(valueBoxX + 0.5, valueBoxY + 0.5, valueBoxWidth, valueBoxHeight, 1, 1, 'F');

      // Caixa principal
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(200, 200, 210);
      doc.setLineWidth(0.2);
      doc.roundedRect(valueBoxX, valueBoxY, valueBoxWidth, valueBoxHeight, 1, 1, 'FD');

      // Valor na caixa
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 70);
      doc.text(item.value.toFixed(1), valueBoxX + valueBoxWidth / 2, valueBoxY + 4, { align: 'center' });

      // Label abaixo
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 90);
      const labelLines = doc.splitTextToSize(item.label, barWidth + 5);
      doc.text(labelLines, barX + barWidth / 2, y + height + 5, { align: 'center' });
    });

    // Label do eixo Y
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 90);
    doc.text(yAxisLabel, x - 5, y + height / 2, { align: 'center', angle: 90 });
  }

  /**
   * Desenha um medidor (gauge) circular aprimorado com efeito 3D e cores pastel
   */
  private static drawGauge(
    doc: jsPDF,
    x: number,
    y: number,
    radius: number,
    value: number,
    maxValue: number,
    label: string,
    ranges: { min: number; max: number; color: [number, number, number]; label: string }[]
  ): void {
    const centerX = x + radius;
    const centerY = y + radius;
    const startAngle = 180;
    const totalAngle = 180;
    const arcWidth = 12;

    // Converter cores RGB para tons pastel mais suaves
    const toPastel = (rgb: [number, number, number]): [number, number, number] => {
      return [
        Math.min(255, rgb[0] + (255 - rgb[0]) * 0.4),
        Math.min(255, rgb[1] + (255 - rgb[1]) * 0.4),
        Math.min(255, rgb[2] + (255 - rgb[2]) * 0.4)
      ];
    };

    // Sombra de base (efeito 3D)
    doc.setFillColor(230, 230, 235);
    doc.circle(centerX + 1, centerY + 2, radius + 2, 'F');

    // Fundo branco do gauge
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(240, 240, 245);
    doc.setLineWidth(0.3);
    doc.circle(centerX, centerY, radius, 'FD');

    // Desenhar arcos coloridos com efeito 3D
    const steps = 60;
    ranges.forEach(range => {
      const rangeStartAngle = startAngle - ((range.min / maxValue) * totalAngle);
      const rangeEndAngle = startAngle - ((range.max / maxValue) * totalAngle);
      const pastelColor = toPastel(range.color);

      // Camada de sombra (inferior)
      for (let i = 0; i < steps; i++) {
        const progress = i / steps;
        const angle1 = (rangeStartAngle - progress * (rangeStartAngle - rangeEndAngle)) * Math.PI / 180;
        const angle2 = (rangeStartAngle - (progress + 1 / steps) * (rangeStartAngle - rangeEndAngle)) * Math.PI / 180;

        // Sombra
        doc.setDrawColor(pastelColor[0] * 0.7, pastelColor[1] * 0.7, pastelColor[2] * 0.7);
        doc.setLineWidth(arcWidth + 1);

        const shadowRadius = radius - arcWidth / 2;
        const sx1 = centerX + shadowRadius * Math.cos(angle1);
        const sy1 = centerY - shadowRadius * Math.sin(angle1) + 1;
        const sx2 = centerX + shadowRadius * Math.cos(angle2);
        const sy2 = centerY - shadowRadius * Math.sin(angle2) + 1;

        doc.line(sx1, sy1, sx2, sy2);
      }

      // Camada principal com gradiente
      for (let i = 0; i < steps; i++) {
        const progress = i / steps;
        const angle1 = (rangeStartAngle - progress * (rangeStartAngle - rangeEndAngle)) * Math.PI / 180;
        const angle2 = (rangeStartAngle - (progress + 1 / steps) * (rangeStartAngle - rangeEndAngle)) * Math.PI / 180;

        // Gradiente suave
        const gradientFactor = 0.85 + 0.15 * Math.sin(progress * Math.PI);
        doc.setDrawColor(
          pastelColor[0] * gradientFactor,
          pastelColor[1] * gradientFactor,
          pastelColor[2] * gradientFactor
        );
        doc.setLineWidth(arcWidth);

        const mainRadius = radius - arcWidth / 2;
        const x1 = centerX + mainRadius * Math.cos(angle1);
        const y1 = centerY - mainRadius * Math.sin(angle1);
        const x2 = centerX + mainRadius * Math.cos(angle2);
        const y2 = centerY - mainRadius * Math.sin(angle2);

        doc.line(x1, y1, x2, y2);
      }

      // Brilho superior (efeito de luz)
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(1.5);
      for (let i = 0; i < steps / 3; i++) {
        const progress = i / steps;
        const angle1 = (rangeStartAngle - progress * (rangeStartAngle - rangeEndAngle)) * Math.PI / 180;
        const angle2 = (rangeStartAngle - (progress + 1 / steps) * (rangeStartAngle - rangeEndAngle)) * Math.PI / 180;

        const highlightRadius = radius - arcWidth / 2 + 4;
        const hx1 = centerX + highlightRadius * Math.cos(angle1);
        const hy1 = centerY - highlightRadius * Math.sin(angle1) - 0.5;
        const hx2 = centerX + highlightRadius * Math.cos(angle2);
        const hy2 = centerY - highlightRadius * Math.sin(angle2) - 0.5;

        doc.line(hx1, hy1, hx2, hy2);
      }
    });

    // Marcações de escala
    doc.setDrawColor(180, 180, 190);
    doc.setLineWidth(0.5);
    const numTicks = 10;
    for (let i = 0; i <= numTicks; i++) {
      const tickAngle = (startAngle - (i / numTicks) * totalAngle) * Math.PI / 180;
      const innerRadius = radius - arcWidth - 3;
      const outerRadius = radius - arcWidth + 2;

      const x1 = centerX + innerRadius * Math.cos(tickAngle);
      const y1 = centerY - innerRadius * Math.sin(tickAngle);
      const x2 = centerX + outerRadius * Math.cos(tickAngle);
      const y2 = centerY - outerRadius * Math.sin(tickAngle);

      doc.line(x1, y1, x2, y2);
    }

    // Ponteiro estilo agulha com sombra
    const valueAngle = (startAngle - (value / maxValue) * totalAngle) * Math.PI / 180;
    const pointerLength = radius - arcWidth - 8;
    const pointerX = centerX + pointerLength * Math.cos(valueAngle);
    const pointerY = centerY - pointerLength * Math.sin(valueAngle);

    // Sombra do ponteiro
    doc.setDrawColor(100, 100, 110);
    doc.setLineWidth(3);
    doc.line(centerX + 0.5, centerY + 0.5, pointerX + 0.5, pointerY + 0.5);

    // Ponteiro principal (gradiente)
    doc.setDrawColor(60, 60, 70);
    doc.setLineWidth(2.5);
    doc.line(centerX, centerY, pointerX, pointerY);

    // Ponta do ponteiro
    doc.setFillColor(200, 60, 70);
    doc.circle(pointerX, pointerY, 2, 'F');

    // Centro decorativo do ponteiro (efeito 3D)
    doc.setFillColor(220, 220, 230);
    doc.circle(centerX + 0.5, centerY + 0.5, 5.5, 'F');

    doc.setFillColor(245, 245, 250);
    doc.circle(centerX, centerY, 5, 'F');

    doc.setFillColor(60, 60, 70);
    doc.circle(centerX, centerY, 3.5, 'F');

    doc.setFillColor(200, 200, 210);
    doc.circle(centerX - 0.5, centerY - 0.5, 1.5, 'F');

    // Caixa de valor com fundo suave
    const boxWidth = 28;
    const boxHeight = 12;
    const boxY = centerY + 6;

    // Sombra da caixa
    doc.setFillColor(220, 220, 230);
    doc.roundedRect(centerX - boxWidth / 2 + 0.5, boxY + 0.5, boxWidth, boxHeight, 2, 2, 'F');

    // Caixa principal
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(200, 200, 210);
    doc.setLineWidth(0.3);
    doc.roundedRect(centerX - boxWidth / 2, boxY, boxWidth, boxHeight, 2, 2, 'FD');

    // Valor no centro
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 60, 70);
    doc.text(value.toFixed(0), centerX, boxY + 8, { align: 'center' });

    // Label
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 90);
    doc.text(label, centerX, centerY + 22, { align: 'center' });

    // Legenda moderna com cores pastel
    let legendY = y + radius * 2 + 10;
    doc.setFontSize(7.5);
    ranges.forEach(range => {
      const pastelColor = toPastel(range.color);

      // Sombra do quadrado
      doc.setFillColor(220, 220, 230);
      doc.roundedRect(x + 0.5, legendY - 1.5, 6, 4, 0.5, 0.5, 'F');

      // Quadrado de cor
      doc.setFillColor(pastelColor[0], pastelColor[1], pastelColor[2]);
      doc.setDrawColor(pastelColor[0] * 0.8, pastelColor[1] * 0.8, pastelColor[2] * 0.8);
      doc.setLineWidth(0.2);
      doc.roundedRect(x, legendY - 2, 6, 4, 0.5, 0.5, 'FD');

      doc.setTextColor(60, 60, 70);
      doc.text(`${range.label} (${range.min}-${range.max})`, x + 8, legendY);
      legendY += 5.5;
    });
  }

  /**
   * Renderiza tabela minuto a minuto com dados de FC, SpO2, distância acumulada e velocidade
   */
  private static renderMinuteByMinuteTable(
    doc: jsPDF,
    attempt: any,
    yPos: number,
    pageWidth: number
  ): number {
    const perMinute = attempt.per_minute || [];
    const perMinuteDistance = attempt.per_minute_distance_m || [];
    const perMinuteSpeed = attempt.per_minute_speed_kmh || [];

    // Se não houver dados, retornar posição atual
    if (perMinute.length === 0) {
      return yPos;
    }

    // Título da seção
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Evolução Minuto a Minuto:', 15, yPos);
    yPos += 7;

    // Cabeçalho da tabela
    doc.setFillColor(240, 240, 240);
    doc.rect(15, yPos - 5, pageWidth - 30, 8, 'F');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Min', 20, yPos);
    doc.text('FC (bpm)', 50, yPos);
    this.renderSpO2Text(doc, 85, yPos, 'left');
    doc.text(' (%)', 85 + doc.getTextWidth('SpO') + 3, yPos);
    doc.text('Dist. acum. (m)', 120, yPos);
    doc.text('Vel. (km/h)', 165, yPos);
    yPos += 8;

    // Linhas da tabela (6 minutos)
    doc.setFont('helvetica', 'normal');
    for (let minute = 0; minute < 6; minute++) {
      const minuteData = perMinute[minute];
      const distance = perMinuteDistance[minute];
      const speed = perMinuteSpeed[minute];

      // Linha alternada
      if (minute % 2 === 1) {
        doc.setFillColor(250, 250, 250);
        doc.rect(15, yPos - 5, pageWidth - 30, 7, 'F');
      }

      // Número do minuto
      doc.setTextColor(0, 0, 0);
      doc.text(`${minute + 1}`, 20, yPos);

      // FC
      const fc = minuteData?.hr || '—';
      doc.text(fc.toString(), 50, yPos);

      // SpO2 (com destaque se crítico)
      const spo2 = minuteData?.spo2;
      let spo2Text = (spo2 !== null && spo2 !== undefined) ? spo2.toString() : '—';

      // Destacar SpO2 < 88%
      if (spo2 !== null && spo2 !== undefined && parseFloat(spo2.toString()) < 88) {
        // Fundo vermelho claro na célula
        doc.setFillColor(255, 240, 240);
        doc.rect(83, yPos - 5, 25, 7, 'F');
        // Texto vermelho
        doc.setTextColor(220, 53, 69);
        doc.setFont('helvetica', 'bold');
        spo2Text = `${spo2} *`;
      }

      doc.text(spo2Text, 85, yPos);

      // Resetar formatação
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');

      // Distância acumulada
      const distText = distance !== undefined && distance !== null ? distance.toString() : '—';
      doc.text(distText, 120, yPos);

      // Velocidade
      const speedText = speed !== undefined && speed !== null ? speed.toFixed(2) : '—';
      doc.text(speedText, 165, yPos);

      yPos += 7;
    }

    yPos += 8; // Espaçamento após a tabela

    return yPos;
  }

  /**
   * Formata uma data para string legível
   */
  private static formatDate(dateValue: any): string {
    try {
      let date: Date;

      if (dateValue instanceof Date) {
        date = dateValue;
      } else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else {
        return 'Data não disponível';
      }

      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Data não disponível';
    }
  }

  /**
   * Gera o PDF completo baseado no tipo de avaliação
   */
  static async generatePDF(assessment: Assessment, previousAssessments?: Assessment[]): Promise<jsPDF> {
    const doc = new jsPDF();

    // Se for avaliação múltipla, gerar relatório combinado
    if (assessment.type === 'MULTI' && assessment.tests && assessment.tests.length > 0) {
      return await this.generateMultiTestReport(doc, assessment, previousAssessments);
    }

    switch (assessment.type) {
      case 'TC6':
        return await this.generateTC6Report(doc, assessment, previousAssessments);
      case 'CAT':
        return await this.generateCATReport(doc, assessment, previousAssessments);
      case 'mMRC':
        return await this.generateMMRCReport(doc, assessment, previousAssessments);
      case 'PFSDQ-M':
        return await this.generatePFSDQMReport(doc, assessment, previousAssessments);
      case '5xSTS':
      case 'STS_30S':
      case 'STS_1MIN':
        return await this.generateSTSReport(doc, assessment, previousAssessments);
      case 'HADS':
        return await this.generateHADSReport(doc, assessment, previousAssessments);
      case 'SGRQ':
        return await this.generateSGRQReport(doc, assessment, previousAssessments);
      case 'LCADL':
        return await this.generateLCADLReport(doc, assessment, previousAssessments);
      case 'MLHFQ':
        return await this.generateMLHFQReport(doc, assessment, previousAssessments);
      case 'PIMAX_SINDEX':
        return await this.generatePImaxSIndexReport(doc, assessment, previousAssessments);
      case 'CPET':
        return await this.generateCPETReport(doc, assessment, previousAssessments);
      default:
        return await this.generateGenericReport(doc, assessment);
    }
  }

  /**
   * Relatório do TC6 (Teste de Caminhada de 6 minutos)
   */
  private static async generateTC6Report(
    doc: jsPDF,
    assessment: Assessment,
    previousAssessments?: Assessment[]
  ): Promise<jsPDF> {
    let yPos = await this.renderHeader(doc, 20);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Título
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 102, 204);
    doc.text('RELATÓRIO - TESTE DE CAMINHADA DE 6 MINUTOS (TC6)', pageWidth / 2, yPos, { align: 'center' });
    yPos += 12;

    // Informações do paciente
    yPos = this.renderPatientInfo(doc, assessment, yPos);

    const tc6Data = assessment.data?.TC6;
    if (!tc6Data || !tc6Data.attempt1) {
      doc.setFontSize(10);
      doc.text('Dados do teste não disponíveis.', 15, yPos);
      this.renderFooter(doc, 1, 1);
      return doc;
    }

    // Processar os dois testes
    const attempts = [tc6Data.attempt1, tc6Data.attempt2].filter(Boolean);

    attempts.forEach((attempt, attemptIndex) => {
      if (yPos > pageHeight - 80) {
        doc.addPage();
        yPos = 20;
      }

      // Título da tentativa
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 102, 204);
      doc.text(`${attemptIndex + 1}ª Tentativa`, 15, yPos);
      yPos += 8;

      // Distância percorrida
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(`Distância Percorrida: ${attempt.total_distance_m} metros`, 15, yPos);
      yPos += 10;

      // Verificar espaço para tabela minuto a minuto
      if (yPos > pageHeight - 70) {
        doc.addPage();
        yPos = 20;
      }

      // Renderizar tabela minuto a minuto
      yPos = this.renderMinuteByMinuteTable(doc, attempt, yPos, pageWidth);

      // Verificar espaço para tabela de sinais vitais
      if (yPos > pageHeight - 60) {
        doc.addPage();
        yPos = 20;
      }

      // Tabela de sinais vitais
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Sinais Vitais:', 15, yPos);
      yPos += 7;

      const baseline = attempt.baseline;
      const final = attempt.final;

      // Cabeçalho da tabela
      doc.setFillColor(240, 240, 240);
      doc.rect(15, yPos - 5, pageWidth - 30, 8, 'F');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Parâmetro', 20, yPos);
      doc.text('Início', 80, yPos);
      doc.text('Fim', 120, yPos);
      doc.text('Variação', 155, yPos);
      yPos += 8;

      // Linhas da tabela
      doc.setFont('helvetica', 'normal');
      const vitalSigns = [
        { label: 'FC (bpm)', start: baseline?.hr, end: final?.hr, useSpO2Label: false },
        { label: 'SpO2 (%)', start: baseline?.spo2, end: final?.spo2, useSpO2Label: true },
        { label: 'PA Sistólica (mmHg)', start: baseline?.bp?.sys, end: final?.bp?.sys, useSpO2Label: false },
        { label: 'PA Diastólica (mmHg)', start: baseline?.bp?.dia, end: final?.bp?.dia, useSpO2Label: false },
        { label: 'Borg Dispneia', start: baseline?.borg?.dyspnea, end: final?.borg?.dyspnea, useSpO2Label: false },
        { label: 'Borg Fadiga', start: baseline?.borg?.fatigue, end: final?.borg?.fatigue, useSpO2Label: false },
      ];

      vitalSigns.forEach((sign, idx) => {
        if (idx % 2 === 1) {
          doc.setFillColor(250, 250, 250);
          doc.rect(15, yPos - 5, pageWidth - 30, 7, 'F');
        }

        if (sign.useSpO2Label) {
          this.renderSpO2Text(doc, 20, yPos, 'left');
          doc.text(' (%)', 20 + doc.getTextWidth('SpO') + 3, yPos);
        } else {
          doc.text(sign.label, 20, yPos);
        }

        doc.text(sign.start?.toString() || '-', 80, yPos);
        doc.text(sign.end?.toString() || '-', 120, yPos);

        if (sign.start && sign.end) {
          const variation = parseFloat(sign.end) - parseFloat(sign.start);
          const variationText = variation > 0 ? `+${variation.toFixed(1)}` : variation.toFixed(1);
          doc.text(variationText, 155, yPos);
        } else {
          doc.text('-', 155, yPos);
        }

        yPos += 7;
      });

      yPos += 5;

      // Verificar dessaturação
      const perMinute = attempt.per_minute || [];
      const spo2Values = perMinute.map((m: any) => parseFloat(m.spo2)).filter((v: number) => !isNaN(v));
      const baselineSpo2 = parseFloat(baseline?.spo2 || '100');
      const minSpo2 = Math.min(...spo2Values, baselineSpo2);
      const spo2Drop = baselineSpo2 - minSpo2;
      const hasDessaturation = spo2Drop > 4 || minSpo2 < 85;

      if (hasDessaturation) {
        doc.setFillColor(255, 240, 240);
        doc.setDrawColor(220, 53, 69);
        doc.setLineWidth(1);
        doc.rect(15, yPos - 3, pageWidth - 30, 15, 'FD');

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(220, 53, 69);
        doc.text('DESSATURACAO DETECTADA', 20, yPos + 3);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(`Queda de `, 20, yPos + 9);
        const quedaX = 20 + doc.getTextWidth('Queda de ');
        this.renderSpO2Text(doc, quedaX, yPos + 9, 'left');
        const spo2Width = doc.getTextWidth('SpO') + 3;
        doc.text(`: ${spo2Drop.toFixed(1)}% | `, quedaX + spo2Width, yPos + 9);
        const minimaX = quedaX + spo2Width + doc.getTextWidth(`: ${spo2Drop.toFixed(1)}% | `);
        this.renderSpO2Text(doc, minimaX, yPos + 9, 'left');
        doc.text(` minima: ${minSpo2.toFixed(1)}%`, minimaX + spo2Width, yPos + 9);

        yPos += 18;
      }

      // Paradas
      const stopReport = attempt.stop_report;
      if (stopReport && stopReport.intervals && stopReport.intervals.length > 0) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Paradas Durante o Teste:', 15, yPos);
        yPos += 7;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(`Total de paradas: ${stopReport.intervals.length}`, 20, yPos);
        yPos += 5;
        doc.text(`Tempo total parado: ${stopReport.total_stopped_fmt}`, 20, yPos);
        yPos += 7;

        stopReport.intervals.forEach((interval: any, idx: number) => {
          doc.text(`${idx + 1}. De ${interval.from_fmt} até ${interval.to_fmt} (duração: ${interval.duration_fmt})`, 25, yPos);
          yPos += 5;
        });

        yPos += 5;
      }

      // Gráficos - Nova página se necessário
      if (yPos > pageHeight - 100) {
        doc.addPage();
        yPos = 20;
      }

      // Preparar dados para o gráfico
      const xLabels = ['0', '1', '2', '3', '4', '5', '6'];
      const fcValues = [
        parseFloat(baseline?.hr || '0'),
        ...perMinute.map((m: any) => parseFloat(m.hr) || null)
      ];
      const spo2ValuesForChart = [
        parseFloat(baseline?.spo2 || '0'),
        ...perMinute.map((m: any) => parseFloat(m.spo2) || null)
      ];

      // Gráfico de FC e SpO2 com eixos Y duplos
      // Calcular escalas apropriadas para cada variável
      const validFcValues = fcValues.filter(v => v !== null && !isNaN(v) && v > 0);
      const validSpo2Values = spo2ValuesForChart.filter(v => v !== null && !isNaN(v) && v > 0);

      const fcMin = validFcValues.length > 0 ? Math.floor(Math.min(...validFcValues) * 0.9) : 60;
      const fcMax = validFcValues.length > 0 ? Math.ceil(Math.max(...validFcValues) * 1.1) : 120;
      const spo2Min = 80;  // Escala fixa para SpO2 (80-100)
      const spo2Max = 100;

      // Apenas adicionar linha de dessaturação se houver dessaturação detectada
      const horizontalLines = hasDessaturation ? [
        { value: 85, color: [255, 0, 0] as [number, number, number], label: 'Dessaturacao', dash: true }
      ] : [];

      this.drawLineChart(
        doc,
        15,
        yPos,
        pageWidth - 30,
        60,
        [
          { label: 'FC', values: fcValues, color: [220, 53, 69], unit: 'bpm', yMin: fcMin, yMax: fcMax },
          { label: 'SpO2', values: spo2ValuesForChart, color: [0, 123, 255], unit: '%', yMin: spo2Min, yMax: spo2Max }
        ],
        xLabels,
        `Evolução dos Sinais Vitais - ${attemptIndex + 1}ª Tentativa`,
        'Valores',
        undefined,
        undefined,
        horizontalLines
      );

      yPos += 80;

      if (attemptIndex < attempts.length - 1) {
        yPos += 10;
      }
    });

    // Gráfico de evolução da distância (se houver avaliações anteriores)
    if (previousAssessments && previousAssessments.length > 0) {
      if (yPos > pageHeight - 100) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 102, 204);
      doc.text('Evolução da Distância Percorrida', 15, yPos);
      yPos += 10;

      // Coletar dados históricos
      const historicalData: { label: string; value: number; color?: [number, number, number] }[] = [];
      
      previousAssessments
        .filter(a => a.type === 'TC6' && a.data?.TC6)
        .slice(-5) // Últimas 5 avaliações
        .forEach(a => {
          const date = new Date(a.datetime || a.createdAt);
          const dateLabel = date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
          const distance = Math.max(
            a.data?.TC6?.attempt1?.total_distance_m || 0,
            a.data?.TC6?.attempt2?.total_distance_m || 0
          );
          historicalData.push({ label: dateLabel, value: distance });
        });

      // Adicionar avaliação atual
      const currentDate = new Date(assessment.datetime || assessment.createdAt);
      const currentDateLabel = currentDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      const currentDistance = Math.max(
        tc6Data.attempt1?.total_distance_m || 0,
        tc6Data.attempt2?.total_distance_m || 0
      );

      this.drawBarChart(
        doc,
        15,
        yPos,
        pageWidth - 30,
        50,
        historicalData,
        'Histórico de Distância Percorrida (metros)',
        'Distância (m)'
      );

      yPos += 70;
    }

    // Observações
    if (assessment.notes) {
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('Observações e Recomendações:', 15, yPos);
      yPos += 7;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const notesLines = doc.splitTextToSize(assessment.notes, pageWidth - 30);
      doc.text(notesLines, 15, yPos);
    }

    this.renderFooter(doc, 1, 1);
    return doc;
  }

  /**
   * Relatório do CAT (COPD Assessment Test)
   */
  private static async generateCATReport(
    doc: jsPDF,
    assessment: Assessment,
    previousAssessments?: Assessment[]
  ): Promise<jsPDF> {
    let yPos = await this.renderHeader(doc, 20);
    const pageWidth = doc.internal.pageSize.getWidth();

    // Título
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 102, 204);
    doc.text('RELATÓRIO - COPD ASSESSMENT TEST (CAT)', pageWidth / 2, yPos, { align: 'center' });
    yPos += 12;

    // Informações do paciente
    yPos = this.renderPatientInfo(doc, assessment, yPos);

    const catData = assessment.data?.CAT;
    if (!catData) {
      doc.setFontSize(10);
      doc.text('Dados do teste não disponíveis.', 15, yPos);
      this.renderFooter(doc, 1, 1);
      return doc;
    }

    const total = catData.total || 0;

    // Medidor de impacto
    const impactRanges = [
      { min: 0, max: 10, color: [40, 167, 69] as [number, number, number], label: 'Baixo' },
      { min: 10, max: 20, color: [255, 193, 7] as [number, number, number], label: 'Médio' },
      { min: 20, max: 30, color: [255, 152, 0] as [number, number, number], label: 'Alto' },
      { min: 30, max: 40, color: [220, 53, 69] as [number, number, number], label: 'Muito Alto' }
    ];

    this.drawGauge(doc, 15, yPos, 35, total, 40, 'Pontuação Total', impactRanges);

    // Interpretação ao lado do medidor
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Interpretação:', 100, yPos + 10);
    yPos += 17;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    let impactLevel = 'Baixo';
    if (total > 30) impactLevel = 'Muito Alto';
    else if (total > 20) impactLevel = 'Alto';
    else if (total > 10) impactLevel = 'Médio';

    doc.text(`Nível de Impacto: ${impactLevel}`, 100, yPos);
    yPos += 7;

    const interpretationText = [
      'O CAT avalia o impacto da DPOC na vida',
      'do paciente. Pontuações mais altas indicam',
      'maior impacto da doença no dia a dia.'
    ];
    doc.setFontSize(9);
    interpretationText.forEach(line => {
      doc.text(line, 100, yPos);
      yPos += 5;
    });

    yPos += 50;

    // Gráfico de barras das respostas
    if (catData.answers && catData.answers.length === 8) {
      const questions = [
        'Tosse',
        'Catarro',
        'Pressão no peito',
        'Falta de ar',
        'Limitação em casa',
        'Confiança para sair',
        'Sono',
        'Energia'
      ];

      const barData = catData.answers.map((value: number, idx: number) => ({
        label: questions[idx],
        value: value,
        color: value >= 4 ? [220, 53, 69] as [number, number, number] : [0, 102, 204] as [number, number, number]
      }));

      this.drawBarChart(
        doc,
        15,
        yPos,
        pageWidth - 30,
        60,
        barData,
        'Pontuação por Item (0-5)',
        'Pontos'
      );

      yPos += 80;
    }

    // Evolução temporal
    if (previousAssessments && previousAssessments.length > 0) {
      const historicalData: { label: string; value: number; color?: [number, number, number] }[] = [];
      
      previousAssessments
        .filter(a => a.type === 'CAT' && a.data?.CAT)
        .slice(-5)
        .forEach(a => {
          const date = new Date(a.datetime || a.createdAt);
          const dateLabel = date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
          historicalData.push({ label: dateLabel, value: a.data?.CAT?.total || 0 });
        });

      const currentDate = new Date(assessment.datetime || assessment.createdAt);
      const currentDateLabel = currentDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      historicalData.push({ label: currentDateLabel, value: total, color: [0, 102, 204] });

      this.drawBarChart(
        doc,
        15,
        yPos,
        pageWidth - 30,
        50,
        historicalData,
        'Evolução da Pontuação CAT',
        'Pontos'
      );
    }

    this.renderFooter(doc, 1, 1);
    return doc;
  }

  /**
   * Relatório genérico (fallback)
   */
  private static async generateGenericReport(doc: jsPDF, assessment: Assessment): Promise<jsPDF> {
    let yPos = await this.renderHeader(doc, 20);
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 102, 204);
    doc.text(`RELATÓRIO - ${assessment.type}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 12;

    yPos = this.renderPatientInfo(doc, assessment, yPos);

    if (assessment.data) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Resultados:', 15, yPos);
      yPos += 7;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const dataText = JSON.stringify(assessment.data, null, 2);
      const lines = doc.splitTextToSize(dataText, pageWidth - 30);
      doc.text(lines, 15, yPos);
    }

    this.renderFooter(doc, 1, 1);
    return doc;
  }

  /**
   * Relatório do mMRC (Modified Medical Research Council)
   */
  private static async generateMMRCReport(
    doc: jsPDF,
    assessment: Assessment,
    previousAssessments?: Assessment[]
  ): Promise<jsPDF> {
    let yPos = await this.renderHeader(doc, 20);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 102, 204);
    doc.text('RELATÓRIO - ESCALA mMRC DE DISPNEIA', pageWidth / 2, yPos, { align: 'center' });
    yPos += 12;

    yPos = this.renderPatientInfo(doc, assessment, yPos);

    const mmrcData = assessment.data?.mMRC;
    if (!mmrcData) {
      doc.setFontSize(10);
      doc.text('Dados do teste não disponíveis.', 15, yPos);
      this.renderFooter(doc, 1, 1);
      return doc;
    }

    const grade = mmrcData.grade || 1;

    // Medidor de grau
    const gradeRanges = [
      { min: 0, max: 1, color: [40, 167, 69] as [number, number, number], label: 'Grau 1' },
      { min: 1, max: 2, color: [139, 195, 74] as [number, number, number], label: 'Grau 2' },
      { min: 2, max: 3, color: [255, 193, 7] as [number, number, number], label: 'Grau 3' },
      { min: 3, max: 4, color: [255, 152, 0] as [number, number, number], label: 'Grau 4' },
      { min: 4, max: 5, color: [220, 53, 69] as [number, number, number], label: 'Grau 5' }
    ];

    this.drawGauge(doc, 15, yPos, 35, grade, 5, 'Grau de Dispneia', gradeRanges);

    // Descrição ao lado
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Descrição:', 100, yPos + 10);
    yPos += 17;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const description = mmrcData.description || '';
    const descLines = doc.splitTextToSize(description, pageWidth - 110);
    doc.text(descLines, 100, yPos);

    yPos += 60;

    // Interpretação
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Interpretação Clínica:', 15, yPos);
    yPos += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const interpretations = [
      'A escala mMRC avalia o grau de limitação causado pela dispneia.',
      'Graus mais altos indicam maior limitação funcional.',
      '',
      'Grau 1-2: Dispneia leve a moderada',
      'Grau 3-4: Dispneia importante',
      'Grau 5: Dispneia muito grave'
    ];
    interpretations.forEach(line => {
      doc.text(line, 15, yPos);
      yPos += 5;
    });

    yPos += 10;

    // Evolução temporal
    if (previousAssessments && previousAssessments.length > 0) {
      const historicalData: { label: string; value: number; color?: [number, number, number] }[] = [];
      
      previousAssessments
        .filter(a => a.type === 'mMRC' && a.data?.mMRC)
        .slice(-5)
        .forEach(a => {
          const date = new Date(a.datetime || a.createdAt);
          const dateLabel = date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
          historicalData.push({ label: dateLabel, value: a.data?.mMRC?.grade || 0 });
        });

      const currentDate = new Date(assessment.datetime || assessment.createdAt);
      const currentDateLabel = currentDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      historicalData.push({ label: currentDateLabel, value: grade, color: [0, 102, 204] });

      this.drawBarChart(
        doc,
        15,
        yPos,
        pageWidth - 30,
        50,
        historicalData,
        'Evolução do Grau de Dispneia',
        'Grau',
        5
      );
    }

    if (assessment.notes) {
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = 20;
      }

      yPos += 70;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Observações:', 15, yPos);
      yPos += 7;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const notesLines = doc.splitTextToSize(assessment.notes, pageWidth - 30);
      doc.text(notesLines, 15, yPos);
    }

    this.renderFooter(doc, 1, 1);
    return doc;
  }

  /**
   * Relatório do PFSDQ-M
   */
  private static async generatePFSDQMReport(
    doc: jsPDF,
    assessment: Assessment,
    previousAssessments?: Assessment[]
  ): Promise<jsPDF> {
    let yPos = await this.renderHeader(doc, 20);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 102, 204);
    doc.text('RELATÓRIO - PFSDQ-M', pageWidth / 2, yPos, { align: 'center' });
    doc.setFontSize(10);
    yPos += 7;
    doc.text('(Pulmonary Functional Status and Dyspnea Questionnaire - Modified)', pageWidth / 2, yPos, { align: 'center' });
    yPos += 12;

    yPos = this.renderPatientInfo(doc, assessment, yPos);

    // O cadastro salva o teste com a chave "PFSDQ-M", mas existem variações
    // legadas/normalizadas. Aceitamos todas para não quebrar a impressão.
    const pfsdqData = this.extractPFSDQPayload(assessment);
    if (!pfsdqData) {
      doc.setFontSize(10);
      doc.text('Dados do teste não disponíveis.', 15, yPos);
      this.renderFooter(doc, 1, 1);
      return doc;
    }

    const dyspneaScore = pfsdqData.dyspnea?.score || 0;
    const fatigueScore = pfsdqData.fatigue?.score || 0;
    const changeScore = pfsdqData.change?.score || 0;
    const totalScore = pfsdqData.total || 0;

    // Cartões de resumo
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Resumo dos Domínios:', 15, yPos);
    yPos += 10;

    const domains = [
      { label: 'Dispneia', score: dyspneaScore, color: [220, 53, 69] },
      { label: 'Fadiga', score: fatigueScore, color: [255, 152, 0] },
      { label: 'Mudança nas Atividades', score: changeScore, color: [0, 123, 255] }
    ];

    const cardWidth = (pageWidth - 45) / 3;
    domains.forEach((domain, idx) => {
      const cardX = 15 + idx * (cardWidth + 5);
      
      doc.setFillColor(domain.color[0], domain.color[1], domain.color[2]);
      doc.setDrawColor(domain.color[0], domain.color[1], domain.color[2]);
      doc.roundedRect(cardX, yPos, cardWidth, 25, 3, 3, 'FD');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.text(domain.label, cardX + cardWidth / 2, yPos + 8, { align: 'center' });
      
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(domain.score.toString(), cardX + cardWidth / 2, yPos + 18, { align: 'center' });
      
      doc.setFontSize(7);
      doc.text('/ 100', cardX + cardWidth / 2, yPos + 22, { align: 'center' });
    });

    yPos += 35;

    // Pontuação total
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`Pontuação Total: ${totalScore} / 300`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    // Gráfico de barras comparativo
    const barData = [
      { label: 'Dispneia', value: dyspneaScore, color: [220, 53, 69] as [number, number, number] },
      { label: 'Fadiga', value: fatigueScore, color: [255, 152, 0] as [number, number, number] },
      { label: 'Mudança', value: changeScore, color: [0, 123, 255] as [number, number, number] }
    ];

    this.drawBarChart(
      doc,
      15,
      yPos,
      pageWidth - 30,
      60,
      barData,
      'Comparação entre Domínios',
      'Pontuação',
      100
    );

    yPos += 80;

    // Interpretação
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Interpretação:', 15, yPos);
    yPos += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const interpretation = [
      'O PFSDQ-M avalia três domínios relacionados à função pulmonar:',
      '• Dispneia: Sensação de falta de ar durante atividades',
      '• Fadiga: Cansaço e falta de energia',
      '• Mudança nas Atividades: Alterações no padrão de atividades diárias',
      '',
      'Pontuações mais altas indicam maior comprometimento funcional.'
    ];
    interpretation.forEach(line => {
      doc.text(line, 15, yPos);
      yPos += 5;
    });

    if (assessment.notes) {
      yPos += 10;
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Observações:', 15, yPos);
      yPos += 7;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const notesLines = doc.splitTextToSize(assessment.notes, pageWidth - 30);
      doc.text(notesLines, 15, yPos);
    }

    this.renderFooter(doc, 1, 1);
    return doc;
  }

  /**
   * Relatório dos Testes STS (Sentar e Levantar)
   */
  private static async generateSTSReport(
    doc: jsPDF,
    assessment: Assessment,
    previousAssessments?: Assessment[]
  ): Promise<jsPDF> {
    let yPos = await this.renderHeader(doc, 20);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const testType = assessment.type;
    const testLabels: Record<string, string> = {
      '5xSTS': 'Teste de Sentar e Levantar 5 Vezes',
      'STS_30S': 'Teste de Sentar e Levantar em 30 Segundos',
      'STS_1MIN': 'Teste de Sentar e Levantar em 1 Minuto'
    };

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 102, 204);
    doc.text(`RELATÓRIO - ${testLabels[testType] || testType}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 12;

    yPos = this.renderPatientInfo(doc, assessment, yPos);

    const rawStsData = assessment.data?.[testType];
    const stsData = this.normalizeSTSData(testType, rawStsData);
    if (!stsData) {
      doc.setFontSize(10);
      doc.text('Dados do teste não disponíveis.', 15, yPos);
      this.renderFooter(doc, 1, 1);
      return doc;
    }

    // Resultado principal
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    
    const valueFormatter = (val: number | null | undefined, unit?: string, digits = 0) => {
      if (typeof val !== 'number' || Number.isNaN(val)) return '—';
      return unit ? `${val.toFixed(digits)} ${unit}` : val.toFixed(digits);
    };

    const ensureSpace = (needed = 8) => {
      if (yPos + needed > pageHeight - 20) {
        doc.addPage();
        yPos = 20;
      }
    };

    if (testType === '5xSTS') {
      const time = stsData.time_seconds || 0;
      doc.text(`Tempo: ${time.toFixed(2)} segundos`, pageWidth / 2, yPos, { align: 'center' });
    } else {
      const repetitions = stsData.repetitions || 0;
      doc.text(`Repetições: ${repetitions}`, pageWidth / 2, yPos, { align: 'center' });
    }
    yPos += 15;

    // Detalhes do teste
    ensureSpace(40);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Resumo do teste:', 15, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');

    if (testType === '5xSTS') {
      const attemptsLabel = stsData.validAttempts?.length
        ? stsData.validAttempts.map((v: number) => `${v.toFixed(2)}s`).join(' · ')
        : '—';
      doc.text(`Tentativas válidas: ${attemptsLabel}`, 15, yPos);
      yPos += 5;
      if (typeof stsData.best === 'number') {
        doc.text(`Melhor tempo: ${stsData.best.toFixed(2)} s`, 15, yPos);
        yPos += 5;
      }
      if (typeof stsData.chairHeightCm === 'number') {
        doc.text(`Altura da cadeira: ${stsData.chairHeightCm.toFixed(0)} cm`, 15, yPos);
        yPos += 5;
      }
      if (typeof stsData.usedArms === 'boolean') {
        doc.text(`Uso dos braços: ${stsData.usedArms ? 'Sim' : 'Não'}`, 15, yPos);
        yPos += 5;
      }
    } else {
      const attemptsLabel = stsData.validAttempts?.length
        ? stsData.validAttempts.map((v: number) => `${v} rep`).join(' · ')
        : '—';
      doc.text(`Tentativas válidas: ${attemptsLabel}`, 15, yPos);
      yPos += 5;
      if (typeof stsData.best === 'number') {
        doc.text(`Melhor marca: ${stsData.best} repetições`, 15, yPos);
        yPos += 5;
      }
      if (typeof stsData.chairHeightCm === 'number') {
        doc.text(`Altura da cadeira: ${stsData.chairHeightCm.toFixed(0)} cm`, 15, yPos);
        yPos += 5;
      }
      if (typeof stsData.restIntervalSeconds === 'number') {
        const mins = Math.floor(stsData.restIntervalSeconds / 60);
        const secs = stsData.restIntervalSeconds % 60;
        const restLabel = mins > 0 ? `${mins} min ${secs}s` : `${secs}s`;
        doc.text(`Intervalo entre tentativas: ${restLabel}`, 15, yPos);
        yPos += 5;
      }
    }

    const attemptDetails = (key: 'attempt1' | 'attempt2', label: string) => {
      const attempt = stsData[key];
      if (!attempt) return;
      ensureSpace(40);
      doc.setFont('helvetica', 'bold');
      doc.text(label, 15, yPos);
      yPos += 5;
      doc.setFont('helvetica', 'normal');

      const lines: string[] = [];
      if (typeof attempt.reps === 'number') lines.push(`Repetições: ${attempt.reps}`);
      if (typeof attempt.reps === 'undefined' && typeof attempt.time === 'number') {
        lines.push(`Tempo: ${attempt.time.toFixed(2)} s`);
      }
      if (attempt.spo2Pre != null || attempt.spo2Post != null) {
        lines.push(`SpO₂ pré/pós: ${valueFormatter(attempt.spo2Pre, '%')} / ${valueFormatter(attempt.spo2Post, '%')}`);
      }
      if (attempt.hrPre != null || attempt.hrPost != null) {
        lines.push(`FC pré/pós: ${valueFormatter(attempt.hrPre, 'bpm')} / ${valueFormatter(attempt.hrPost, 'bpm')}`);
      }
      if (attempt.borgDyspneaPre != null || attempt.borgDyspneaPost != null) {
        lines.push(`Borg Dispneia pré/pós: ${attempt.borgDyspneaPre ?? '—'} / ${attempt.borgDyspneaPost ?? '—'}`);
      }
      if (attempt.borgLegPre != null || attempt.borgLegPost != null) {
        lines.push(`Borg Pernas pré/pós: ${attempt.borgLegPre ?? '—'} / ${attempt.borgLegPost ?? '—'}`);
      }

      lines.forEach((text) => {
        ensureSpace(10);
        doc.text(text, 20, yPos);
        yPos += 5;
      });
    };

    attemptDetails('attempt1', '1ª tentativa:');
    attemptDetails('attempt2', '2ª tentativa:');

    yPos += 5;

    // Comparação com valores de referência (se disponível)
    if (stsData.reference) {
      doc.setFillColor(240, 248, 255);
      doc.roundedRect(15, yPos, pageWidth - 30, 30, 3, 3, 'F');

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Comparação com Valores de Referência:', 20, yPos + 8);
      yPos += 15;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`Valor esperado para idade/sexo: ${stsData.reference.expected}`, 20, yPos);
      yPos += 5;
      doc.text(`Classificação: ${stsData.reference.classification}`, 20, yPos);
      yPos += 5;
      
      if (stsData.reference.fallRisk) {
        doc.setTextColor(220, 53, 69);
        doc.setFont('helvetica', 'bold');
        doc.text('⚠ Risco aumentado de quedas', 20, yPos);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
      }

      yPos += 20;
    }

    // Evolução temporal
    if (previousAssessments && previousAssessments.length > 0) {
      const historicalData: { label: string; value: number; color?: [number, number, number] }[] = [];
      
      previousAssessments
        .filter(a => a.type === testType && a.data?.[testType])
        .slice(-5)
        .forEach(a => {
          const date = new Date(a.datetime || a.createdAt);
          const dateLabel = date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
          const prevNormalized = this.normalizeSTSData(testType, a.data?.[testType]);
          const value = testType === '5xSTS' 
            ? prevNormalized?.time_seconds 
            : prevNormalized?.repetitions;
          historicalData.push({ label: dateLabel, value: value || 0 });
        });

      const currentDate = new Date(assessment.datetime || assessment.createdAt);
      const currentDateLabel = currentDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      const currentValue = testType === '5xSTS' ? stsData.time_seconds : stsData.repetitions;
      historicalData.push({ label: currentDateLabel, value: currentValue || 0, color: [0, 102, 204] });

      const chartTitle = testType === '5xSTS' 
        ? 'Evolução do Tempo (segundos)'
        : 'Evolução das Repetições';
      const yLabel = testType === '5xSTS' ? 'Segundos' : 'Repetições';

      this.drawBarChart(
        doc,
        15,
        yPos,
        pageWidth - 30,
        60,
        historicalData,
        chartTitle,
        yLabel
      );

      yPos += 80;
    }

    // Interpretação
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Interpretação:', 15, yPos);
    yPos += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const interpretations: Record<string, string[]> = {
      '5xSTS': [
        'O teste de sentar e levantar 5 vezes avalia a força de membros inferiores.',
        'Tempos maiores indicam menor força muscular e maior risco de quedas.',
        'Valores acima de 12 segundos em idosos indicam risco aumentado.'
      ],
      'STS_30S': [
        'O teste de 30 segundos avalia resistência muscular de membros inferiores.',
        'Mais repetições indicam melhor condicionamento físico.',
        'Valores abaixo da média para idade/sexo podem indicar sarcopenia.'
      ],
      'STS_1MIN': [
        'O teste de 1 minuto avalia capacidade funcional e resistência.',
        'Mais repetições indicam melhor condicionamento físico geral.',
        'Útil para acompanhar evolução em programas de reabilitação.'
      ]
    };

    (interpretations[testType] || []).forEach(line => {
      doc.text(line, 15, yPos);
      yPos += 5;
    });

    if (assessment.notes) {
      yPos += 10;
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Observações:', 15, yPos);
      yPos += 7;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const notesLines = doc.splitTextToSize(assessment.notes, pageWidth - 30);
      doc.text(notesLines, 15, yPos);
    }

    this.renderFooter(doc, 1, 1);
    return doc;
  }

  private static normalizeSTSData(testType: string, raw: any) {
    if (!raw || typeof raw !== 'object') return null;
    const clone: any = { ...raw };

    const toNumber = (value: any): number | null => {
      if (typeof value === 'number' && !Number.isNaN(value)) return value;
      if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      }
      return null;
    };

    const validAttempts = Array.isArray(clone.attempts)
      ? clone.attempts
          .map(toNumber)
          .filter((v: number | null | undefined): v is number => v != null && v > 0)
      : [];

    const bestFromAttempts =
      testType === '5xSTS'
        ? (validAttempts.length ? Math.min(...validAttempts) : null)
        : (validAttempts.length ? Math.max(...validAttempts) : null);

    if (testType === '5xSTS') {
      const best = toNumber(clone.best) ?? bestFromAttempts;
      if (clone.time_seconds == null || Number.isNaN(clone.time_seconds)) {
        if (best != null) clone.time_seconds = best;
      }
    } else {
      const best = toNumber(clone.best) ?? bestFromAttempts;
      if (clone.repetitions == null || Number.isNaN(clone.repetitions)) {
        if (best != null) clone.repetitions = Math.round(best);
        else clone.repetitions = 0;
      }
    }

    const copyAttempt = (attempt: any | undefined) => {
      if (!attempt || typeof attempt !== 'object') return undefined;
      const duplicated = { ...attempt };
      // Normalizar valores numéricos simples (strings -> números) para apresentação
      ['reps', 'spo2Pre', 'spo2Post', 'hrPre', 'hrPost', 'borgDyspneaPre', 'borgDyspneaPost', 'borgLegPre', 'borgLegPost', 'time', 'chairHeightCm'].forEach(
        (field) => {
          if (duplicated[field] != null) {
            const parsed = toNumber(duplicated[field]);
            if (parsed != null) duplicated[field] = parsed;
          }
        }
      );
      return duplicated;
    };

    const attempt1 = copyAttempt(clone.attempt1);
    const attempt2 = copyAttempt(clone.attempt2);

    if (clone.chairHeightCm == null) {
      clone.chairHeightCm = attempt1?.chairHeightCm ?? attempt2?.chairHeightCm ?? null;
    }

    if (clone.restIntervalSeconds != null) {
      const parsed = toNumber(clone.restIntervalSeconds);
      clone.restIntervalSeconds = parsed ?? clone.restIntervalSeconds;
    }

    return {
      ...clone,
      validAttempts,
      attempt1,
      attempt2,
    };
  }

  /**
   * Relatório do HADS
   */
  private static async generateHADSReport(
    doc: jsPDF,
    assessment: Assessment,
    previousAssessments?: Assessment[]
  ): Promise<jsPDF> {
    let yPos = await this.renderHeader(doc, 20);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 102, 204);
    doc.text('RELATÓRIO - HADS', pageWidth / 2, yPos, { align: 'center' });
    doc.setFontSize(10);
    yPos += 7;
    doc.text('(Escala Hospitalar de Ansiedade e Depressão)', pageWidth / 2, yPos, { align: 'center' });
    yPos += 12;

    yPos = this.renderPatientInfo(doc, assessment, yPos);

    const hadsData = assessment.data?.HADS;
    if (!hadsData) {
      doc.setFontSize(10);
      doc.text('Dados do teste não disponíveis.', 15, yPos);
      this.renderFooter(doc, 1, 1);
      return doc;
    }

    const anxietyScore = hadsData.anxietyScore || 0;
    const depressionScore = hadsData.depressionScore || 0;

    // Medidores lado a lado
    const gaugeRanges = [
      { min: 0, max: 7, color: [40, 167, 69] as [number, number, number], label: 'Normal' },
      { min: 7, max: 10, color: [255, 193, 7] as [number, number, number], label: 'Limítrofe' },
      { min: 10, max: 21, color: [220, 53, 69] as [number, number, number], label: 'Anormal' }
    ];

    // Ansiedade
    this.drawGauge(doc, 15, yPos, 35, anxietyScore, 21, 'Ansiedade', gaugeRanges);

    // Depressão
    this.drawGauge(doc, pageWidth / 2 + 10, yPos, 35, depressionScore, 21, 'Depressão', gaugeRanges);

    yPos += 100;

    // Interpretação
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Interpretação dos Resultados:', 15, yPos);
    yPos += 10;

    // Tabela de classificação
    doc.setFillColor(240, 240, 240);
    doc.rect(15, yPos - 5, pageWidth - 30, 8, 'F');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Domínio', 20, yPos);
    doc.text('Pontuação', 80, yPos);
    doc.text('Classificação', 130, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'normal');
    
    // Ansiedade
    doc.text('Ansiedade', 20, yPos);
    doc.text(anxietyScore.toString(), 80, yPos);
    let anxietyClass = 'Normal';
    if (anxietyScore >= 11) anxietyClass = 'Anormal';
    else if (anxietyScore >= 8) anxietyClass = 'Limítrofe';
    doc.text(anxietyClass, 130, yPos);
    yPos += 7;

    // Depressão
    doc.setFillColor(250, 250, 250);
    doc.rect(15, yPos - 5, pageWidth - 30, 7, 'F');
    doc.text('Depressão', 20, yPos);
    doc.text(depressionScore.toString(), 80, yPos);
    let depressionClass = 'Normal';
    if (depressionScore >= 11) depressionClass = 'Anormal';
    else if (depressionScore >= 8) depressionClass = 'Limítrofe';
    doc.text(depressionClass, 130, yPos);
    yPos += 15;

    // Nota importante
    doc.setFillColor(255, 248, 225);
    doc.roundedRect(15, yPos, pageWidth - 30, 25, 3, 3, 'F');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    const note = [
      'Nota: O HADS é uma ferramenta de triagem. Resultados limítrofes ou anormais',
      'sugerem a necessidade de avaliação mais aprofundada por profissional de saúde',
      'mental. Não substitui diagnóstico clínico.'
    ];
    note.forEach((line, idx) => {
      doc.text(line, 20, yPos + 8 + (idx * 5));
    });

    yPos += 35;

    // Evolução temporal
    if (previousAssessments && previousAssessments.length > 0) {
      const anxietyHistory: HistoricalDataEntry[] = [];
      const depressionHistory: HistoricalDataEntry[] = [];
      
      previousAssessments
        .filter(a => a.type === 'HADS' && a.data?.HADS)
        .slice(-5)
        .forEach(a => {
          const date = new Date(a.datetime || a.createdAt);
          const dateLabel = date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
          anxietyHistory.push({ label: dateLabel, value: a.data?.HADS?.anxietyScore || 0 });
          depressionHistory.push({ label: dateLabel, value: a.data?.HADS?.depressionScore || 0 });
        });

      const currentDate = new Date(assessment.datetime || assessment.createdAt);
      const currentDateLabel = currentDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      anxietyHistory.push({ label: currentDateLabel, value: anxietyScore, color: [220, 53, 69] });
      depressionHistory.push({ label: currentDateLabel, value: depressionScore, color: [0, 123, 255] });

      // Gráfico de ansiedade
      this.drawBarChart(
        doc,
        15,
        yPos,
        (pageWidth - 35) / 2,
        50,
        anxietyHistory,
        'Evolução - Ansiedade',
        'Pontos',
        21
      );

      // Gráfico de depressão
      this.drawBarChart(
        doc,
        (pageWidth / 2) + 5,
        yPos,
        (pageWidth - 35) / 2,
        50,
        depressionHistory,
        'Evolução - Depressão',
        'Pontos',
        21
      );

      yPos += 70;
    }

    if (assessment.notes) {
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('Observações:', 15, yPos);
      yPos += 7;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const notesLines = doc.splitTextToSize(assessment.notes, pageWidth - 30);
      doc.text(notesLines, 15, yPos);
    }

    this.renderFooter(doc, 1, 1);
    return doc;
  }

  /**
   * Relatório do SGRQ
   */
  private static async generateSGRQReport(
    doc: jsPDF,
    assessment: Assessment,
    previousAssessments?: Assessment[]
  ): Promise<jsPDF> {
    let yPos = await this.renderHeader(doc, 20);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 102, 204);
    doc.text('RELATÓRIO - SGRQ', pageWidth / 2, yPos, { align: 'center' });
    doc.setFontSize(10);
    yPos += 7;
    doc.text('(Saint George Respiratory Questionnaire)', pageWidth / 2, yPos, { align: 'center' });
    yPos += 12;

    yPos = this.renderPatientInfo(doc, assessment, yPos);

    const sgrqData = assessment.data?.SGRQ;
    if (!sgrqData) {
      doc.setFontSize(10);
      doc.text('Dados do teste não disponíveis.', 15, yPos);
      this.renderFooter(doc, 1, 1);
      return doc;
    }

    const symptomsScore = sgrqData.symptoms || 0;
    const activityScore = sgrqData.activity || 0;
    const impactScore = sgrqData.impact || 0;
    const totalScore = sgrqData.total || 0;

    // Pontuação total em destaque
    doc.setFillColor(0, 102, 204);
    doc.roundedRect(15, yPos, pageWidth - 30, 20, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`Pontuação Total: ${totalScore.toFixed(1)}`, pageWidth / 2, yPos + 13, { align: 'center' });
    yPos += 30;

    // Cartões dos domínios
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Domínios:', 15, yPos);
    yPos += 10;

    const domains = [
      { label: 'Sintomas', score: symptomsScore, color: [220, 53, 69] },
      { label: 'Atividades', score: activityScore, color: [255, 152, 0] },
      { label: 'Impactos', score: impactScore, color: [0, 123, 255] }
    ];

    const cardWidth = (pageWidth - 45) / 3;
    domains.forEach((domain, idx) => {
      const cardX = 15 + idx * (cardWidth + 5);
      
      doc.setDrawColor(domain.color[0], domain.color[1], domain.color[2]);
      doc.setLineWidth(2);
      doc.roundedRect(cardX, yPos, cardWidth, 25, 3, 3, 'D');

      doc.setTextColor(domain.color[0], domain.color[1], domain.color[2]);
      doc.setFontSize(9);
      doc.text(domain.label, cardX + cardWidth / 2, yPos + 8, { align: 'center' });
      
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(domain.score.toFixed(1), cardX + cardWidth / 2, yPos + 20, { align: 'center' });
    });

    yPos += 35;

    // Gráfico de barras
    doc.setTextColor(0, 0, 0);
    const barData = [
      { label: 'Sintomas', value: symptomsScore, color: [220, 53, 69] as [number, number, number] },
      { label: 'Atividades', value: activityScore, color: [255, 152, 0] as [number, number, number] },
      { label: 'Impactos', value: impactScore, color: [0, 123, 255] as [number, number, number] },
      { label: 'Total', value: totalScore, color: [0, 102, 204] as [number, number, number] }
    ];

    this.drawBarChart(
      doc,
      15,
      yPos,
      pageWidth - 30,
      60,
      barData,
      'Pontuação por Domínio',
      'Pontos',
      100
    );

    yPos += 80;

    // Interpretação
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Interpretação:', 15, yPos);
    yPos += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const interpretation = [
      'O SGRQ avalia o impacto da doença respiratória na qualidade de vida.',
      'Pontuações variam de 0 a 100, onde valores mais altos indicam pior qualidade de vida.',
      '',
      '• Sintomas: Frequência e gravidade dos sintomas respiratórios',
      '• Atividades: Limitações causadas pela dispneia',
      '• Impactos: Impacto psicossocial da doença',
      '',
      'Diferenças de 4 pontos ou mais são consideradas clinicamente significativas.'
    ];
    interpretation.forEach(line => {
      doc.text(line, 15, yPos);
      yPos += 5;
    });

    yPos += 10;

    // Evolução temporal
    if (previousAssessments && previousAssessments.length > 0) {
      if (yPos > pageHeight - 80) {
        doc.addPage();
        yPos = 20;
      }

      const historicalData: { label: string; value: number; color?: [number, number, number] }[] = [];
      
      previousAssessments
        .filter(a => a.type === 'SGRQ' && a.data?.SGRQ)
        .slice(-5)
        .forEach(a => {
          const date = new Date(a.datetime || a.createdAt);
          const dateLabel = date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
          historicalData.push({ label: dateLabel, value: a.data?.SGRQ?.total || 0 });
        });

      const currentDate = new Date(assessment.datetime || assessment.createdAt);
      const currentDateLabel = currentDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      historicalData.push({ label: currentDateLabel, value: totalScore, color: [0, 102, 204] });

      this.drawBarChart(
        doc,
        15,
        yPos,
        pageWidth - 30,
        50,
        historicalData,
        'Evolução da Pontuação Total',
        'Pontos',
        100
      );

      yPos += 70;
    }

    if (assessment.notes) {
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Observações:', 15, yPos);
      yPos += 7;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const notesLines = doc.splitTextToSize(assessment.notes, pageWidth - 30);
      doc.text(notesLines, 15, yPos);
    }

    this.renderFooter(doc, 1, 1);
    return doc;
  }

  /**
   * Download do PDF
   */
  static async downloadPDF(assessment: Assessment, previousAssessments?: Assessment[]): Promise<void> {
    try {
      const doc = await this.generatePDF(assessment, previousAssessments);
      const fileName = `Avaliacao_${assessment.type}_${assessment.patientName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      throw new Error(`Erro ao gerar PDF: ${error}`);
    }
  }

  /**
   * Impressão do PDF
   */
  static async printPDF(assessment: Assessment, previousAssessments?: Assessment[]): Promise<void> {
    try {
      const doc = await this.generatePDF(assessment, previousAssessments);
      const blob = doc.output('blob');
      const url = URL.createObjectURL(blob);

      const printWindow = window.open(url, '_blank');

      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            printWindow.onafterprint = () => {
              printWindow.close();
              URL.revokeObjectURL(url);
            };
          }, 500);
        };
      } else {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.top = '-9999px';
        iframe.style.left = '-9999px';
        iframe.style.width = '0px';
        iframe.style.height = '0px';

        document.body.appendChild(iframe);
        iframe.src = url;

        setTimeout(() => {
          iframe.contentWindow?.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
            URL.revokeObjectURL(url);
          }, 1000);
        }, 500);
      }
    } catch (error) {
      console.error('Erro ao imprimir PDF:', error);
      throw new Error(`Erro ao imprimir PDF: ${error}`);
    }
  }

  /**
   * Relatório do LCADL
   */
  private static async generateLCADLReport(
    doc: jsPDF,
    assessment: Assessment,
    previousAssessments?: Assessment[]
  ): Promise<jsPDF> {
    let yPos = await this.renderHeader(doc, 20);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 102, 204);
    doc.text('RELATÓRIO - LCADL', pageWidth / 2, yPos, { align: 'center' });
    doc.setFontSize(10);
    yPos += 7;
    doc.text('(London Chest Activity of Daily Living)', pageWidth / 2, yPos, { align: 'center' });
    yPos += 12;

    yPos = this.renderPatientInfo(doc, assessment, yPos);

    const lcadlData = assessment.data?.LCADL;
    if (!lcadlData) {
      doc.setFontSize(10);
      doc.text('Dados do teste não disponíveis.', 15, yPos);
      this.renderFooter(doc, 1, 1);
      return doc;
    }

    const totalScore = lcadlData.totalScore || 0;
    const maxScore = lcadlData.maxScore || 75;
    const percentage = lcadlData.percentage || 0;

    // Pontuação total em destaque
    doc.setFillColor(0, 102, 204);
    doc.roundedRect(15, yPos, pageWidth - 30, 25, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`Pontuação Total: ${totalScore} / ${maxScore}`, pageWidth / 2, yPos + 10, { align: 'center' });
    doc.setFontSize(11);
    doc.text(`(${percentage.toFixed(1)}% de comprometimento)`, pageWidth / 2, yPos + 18, { align: 'center' });
    yPos += 35;

    // Domínios
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Pontuação por Domínio:', 15, yPos);
    yPos += 10;

    const domains = [
      { key: 'selfCare', label: 'Cuidados Pessoais', color: [220, 53, 69] },
      { key: 'domestic', label: 'Atividades Domésticas', color: [255, 152, 0] },
      { key: 'physical', label: 'Atividade Física', color: [0, 123, 255] },
      { key: 'leisure', label: 'Lazer', color: [139, 195, 74] }
    ];

    const barData: { label: string; value: number; color: [number, number, number] }[] = [];
    
    domains.forEach(domain => {
      const domainData = lcadlData[domain.key];
      if (domainData) {
        const score = domainData.score || 0;
        const max = domainData.maxScore || 0;
        const percent = max > 0 ? (score / max) * 100 : 0;
        barData.push({
          label: `${domain.label}\n${score}/${max}`,
          value: percent,
          color: domain.color as [number, number, number]
        });
      }
    });

    this.drawBarChart(
      doc,
      15,
      yPos,
      pageWidth - 30,
      70,
      barData,
      'Comprometimento por Domínio (%)',
      'Percentual',
      100
    );

    yPos += 90;

    // Interpretação
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Interpretação:', 15, yPos);
    yPos += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const interpretation = [
      'O LCADL avalia o impacto da dispneia nas atividades de vida diária.',
      'Pontuações mais altas indicam maior limitação funcional.',
      '',
      '• Cuidados Pessoais: Atividades básicas como vestir-se e secar-se',
      '• Atividades Domésticas: Tarefas do lar como fazer cama e limpar',
      '• Atividade Física: Capacidade de subir escadas e inclinar-se',
      '• Lazer: Impacto nas atividades sociais e de comunicação',
      '',
      'Valores acima de 50% indicam comprometimento significativo.'
    ];
    interpretation.forEach(line => {
      doc.text(line, 15, yPos);
      yPos += 5;
    });

    yPos += 10;

    // Evolução temporal
    if (previousAssessments && previousAssessments.length > 0) {
      if (yPos > pageHeight - 80) {
        doc.addPage();
        yPos = 20;
      }

      const historicalData: { label: string; value: number; color?: [number, number, number] }[] = [];
      
      previousAssessments
        .filter(a => a.type === 'LCADL' && a.data?.LCADL)
        .slice(-5)
        .forEach(a => {
          const date = new Date(a.datetime || a.createdAt);
          const dateLabel = date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
          historicalData.push({ label: dateLabel, value: a.data?.LCADL?.percentage || 0 });
        });

      const currentDate = new Date(assessment.datetime || assessment.createdAt);
      const currentDateLabel = currentDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      historicalData.push({ label: currentDateLabel, value: percentage, color: [0, 102, 204] });

      this.drawBarChart(
        doc,
        15,
        yPos,
        pageWidth - 30,
        50,
        historicalData,
        'Evolução do Comprometimento (%)',
        'Percentual',
        100
      );

      yPos += 70;
    }

    if (assessment.notes) {
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Observações:', 15, yPos);
      yPos += 7;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const notesLines = doc.splitTextToSize(assessment.notes, pageWidth - 30);
      doc.text(notesLines, 15, yPos);
    }

    this.renderFooter(doc, 1, 1);
    return doc;
  }

  /**
   * Relatório do MLHFQ (Minnesota Living with Heart Failure Questionnaire)
   */
  private static async generateMLHFQReport(
    doc: jsPDF,
    assessment: Assessment,
    previousAssessments?: Assessment[]
  ): Promise<jsPDF> {
    let yPos = await this.renderHeader(doc, 20);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 102, 204);
    doc.text('RELATÓRIO - MLHFQ', pageWidth / 2, yPos, { align: 'center' });
    doc.setFontSize(10);
    yPos += 7;
    doc.text('(Minnesota Living with Heart Failure Questionnaire)', pageWidth / 2, yPos, { align: 'center' });
    yPos += 12;

    yPos = this.renderPatientInfo(doc, assessment, yPos);

    const mlhfqData = assessment.data?.MLHFQ;
    if (!mlhfqData) {
      doc.setFontSize(10);
      doc.text('Dados do teste não disponíveis.', 15, yPos);
      this.renderFooter(doc, 1, 1);
      return doc;
    }

    const totalScore = mlhfqData.total || 0;
    const physicalScore = mlhfqData.physical?.score || 0;
    const emotionalScore = mlhfqData.emotional?.score || 0;
    const physicalItems = mlhfqData.physical?.items || [2, 3, 4, 5, 6, 7, 12, 13];
    const emotionalItems = mlhfqData.emotional?.items || [17, 18, 19, 20, 21];

    const otherScore = totalScore - physicalScore - emotionalScore;
    const maxPhysical = physicalItems.length * 5;
    const maxEmotional = emotionalItems.length * 5;
    const maxOther = 21 * 5 - maxPhysical - maxEmotional;

    doc.setFillColor(0, 102, 204);
    doc.roundedRect(15, yPos, pageWidth - 30, 25, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`Pontuação Total: ${totalScore} / 105`, pageWidth / 2, yPos + 10, { align: 'center' });
    doc.setFontSize(11);
    doc.text('(Quanto maior a pontuação, pior a qualidade de vida)', pageWidth / 2, yPos + 18, { align: 'center' });
    yPos += 35;

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Pontuação por Domínio:', 15, yPos);
    yPos += 10;

    const barData: { label: string; value: number; color: [number, number, number] }[] = [
      {
        label: `Física\n${physicalScore}/${maxPhysical}`,
        value: (physicalScore / maxPhysical) * 100,
        color: [220, 53, 69]
      },
      {
        label: `Emocional\n${emotionalScore}/${maxEmotional}`,
        value: (emotionalScore / maxEmotional) * 100,
        color: [255, 152, 0]
      },
      {
        label: `Outros\n${otherScore}/${maxOther}`,
        value: (otherScore / maxOther) * 100,
        color: [0, 123, 255]
      }
    ];

    this.drawBarChart(
      doc,
      15,
      yPos,
      pageWidth - 30,
      70,
      barData,
      'Comprometimento por Domínio (%)',
      'Percentual',
      100
    );

    yPos += 80;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Interpretação dos Domínios:', 15, yPos);
    yPos += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const interpretationLines = [
      `• Domínio Físico (${physicalScore}/${maxPhysical} pontos): Avalia limitações em atividades físicas, sono e fadiga`,
      `• Domínio Emocional (${emotionalScore}/${maxEmotional} pontos): Avalia preocupações, depressão e autocontrole`,
      `• Outros Domínios (${otherScore}/${maxOther} pontos): Inclui aspectos sociais, financeiros e dietéticos`
    ];

    interpretationLines.forEach(line => {
      if (yPos > pageHeight - 30) {
        doc.addPage();
        yPos = 20;
      }
      const lines = doc.splitTextToSize(line, pageWidth - 30);
      doc.text(lines, 15, yPos);
      yPos += lines.length * 5 + 2;
    });

    yPos += 5;

    if (previousAssessments && previousAssessments.length > 0) {
      if (yPos > pageHeight - 100) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 102, 204);
      doc.text('Evolução Histórica', 15, yPos);
      yPos += 10;

      const historicalData: { label: string; values: number[]; color: [number, number, number]; unit: string }[] = [];
      const labels: string[] = [];

      const allAssessments = [...previousAssessments, assessment]
        .filter(a => a.type === 'MLHFQ' && a.data?.MLHFQ)
        .sort((a, b) => {
          const dateA = new Date(a.datetime || a.createdAt).getTime();
          const dateB = new Date(b.datetime || b.createdAt).getTime();
          return dateA - dateB;
        })
        .slice(-5);

      const totalValues: number[] = [];
      const physicalValues: number[] = [];
      const emotionalValues: number[] = [];

      allAssessments.forEach(a => {
        const date = new Date(a.datetime || a.createdAt);
        const dateLabel = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}`;
        labels.push(dateLabel);

        totalValues.push(a.data?.MLHFQ?.total || 0);
        physicalValues.push(a.data?.MLHFQ?.physical?.score || 0);
        emotionalValues.push(a.data?.MLHFQ?.emotional?.score || 0);
      });

      if (labels.length > 1) {
        historicalData.push({
          label: 'Total',
          values: totalValues,
          color: [0, 102, 204],
          unit: 'pontos'
        });
        historicalData.push({
          label: 'Física',
          values: physicalValues,
          color: [220, 53, 69],
          unit: 'pontos'
        });
        historicalData.push({
          label: 'Emocional',
          values: emotionalValues,
          color: [255, 152, 0],
          unit: 'pontos'
        });

        this.drawLineChart(
          doc,
          15,
          yPos,
          pageWidth - 30,
          60,
          historicalData,
          labels,
          'Evolução das Pontuações MLHFQ',
          'Pontos', // Adicionando yAxisLabel
          0,        // Adicionando yMin
          105       // Adicionando yMax
        );

        yPos += 70;
      }
    }

    if (yPos > pageHeight - 60) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 102, 204);
    doc.text('Sobre o MLHFQ:', 15, yPos);
    yPos += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    const aboutText = [
      'O MLHFQ avalia o impacto da insuficiência cardíaca na qualidade de vida.',
      '',
      'Pontuação: 0-105 pontos (quanto maior, pior a qualidade de vida)',
      '• 0-24 pontos: Impacto leve',
      '• 25-45 pontos: Impacto moderado',
      '• > 45 pontos: Impacto severo',
      '',
      'O questionário avalia limitações físicas, emocionais e sociais relacionadas à IC.'
    ];

    aboutText.forEach(line => {
      if (yPos > pageHeight - 20) {
        doc.addPage();
        yPos = 20;
      }
      if (line === '') {
        yPos += 3;
      } else {
        const lines = doc.splitTextToSize(line, pageWidth - 30);
        doc.text(lines, 15, yPos);
        yPos += lines.length * 5;
      }
    });

    if (assessment.notes) {
      yPos += 5;
      if (yPos > pageHeight - 30) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('Observações:', 15, yPos);
      yPos += 7;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const notesLines = doc.splitTextToSize(assessment.notes, pageWidth - 30);
      doc.text(notesLines, 15, yPos);
    }

    this.renderFooter(doc, 1, 1);
    return doc;
  }

  /**
   * Relatório do PImax e S-Index
   */
  private static async generatePImaxSIndexReport(
    doc: jsPDF,
    assessment: Assessment,
    previousAssessments?: Assessment[]
  ): Promise<jsPDF> {
    let yPos = await this.renderHeader(doc, 20);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 102, 204);
    doc.text('RELATÓRIO - PImax E S-INDEX', pageWidth / 2, yPos, { align: 'center' });
    doc.setFontSize(10);
    yPos += 7;
    doc.text('(Pressão Inspiratória Máxima e Sniff Nasal Inspiratory Pressure)', pageWidth / 2, yPos, { align: 'center' });
    yPos += 12;

    yPos = this.renderPatientInfo(doc, assessment, yPos);

    const pimaxData = assessment.data?.PIMAX_SINDEX;
    if (!pimaxData) {
      doc.setFontSize(10);
      doc.text('Dados do teste não disponíveis.', 15, yPos);
      this.renderFooter(doc, 1, 1);
      return doc;
    }

    const maxPimax = pimaxData.pimax?.max || 0;
    const predictedPimax = pimaxData.pimax?.predicted || null;
    const percentPredicted = pimaxData.pimax?.percentPredicted || null;
    const maxSnip = pimaxData.sindex?.max || 0;

    // Cartões de resultados
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Resultados:', 15, yPos);
    yPos += 10;

    // PImax
    const cardWidth = (pageWidth - 35) / 2;
    doc.setFillColor(220, 53, 69);
    doc.roundedRect(15, yPos, cardWidth, 35, 3, 3, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.text('PImax', 15 + cardWidth / 2, yPos + 10, { align: 'center' });
    
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(`${maxPimax.toFixed(1)}`, 15 + cardWidth / 2, yPos + 22, { align: 'center' });
    
    doc.setFontSize(9);
    doc.text('cmH₂O', 15 + cardWidth / 2, yPos + 30, { align: 'center' });

    // S-Index
    doc.setFillColor(0, 123, 255);
    doc.roundedRect(20 + cardWidth, yPos, cardWidth, 35, 3, 3, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.text('S-Index (SNIP)', 20 + cardWidth + cardWidth / 2, yPos + 10, { align: 'center' });
    
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(`${maxSnip.toFixed(1)}`, 20 + cardWidth + cardWidth / 2, yPos + 22, { align: 'center' });
    
    doc.setFontSize(9);
    doc.text('cmH₂O', 20 + cardWidth + cardWidth / 2, yPos + 30, { align: 'center' });

    yPos += 45;

    // Comparação com valores preditos (PImax)
    if (predictedPimax && percentPredicted) {
      doc.setTextColor(0, 0, 0);
      doc.setFillColor(240, 248, 255);
      doc.roundedRect(15, yPos, pageWidth - 30, 30, 3, 3, 'F');

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Comparação com Valores Preditos (PImax):', 20, yPos + 10);
      yPos += 17;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`Valor predito: ${predictedPimax.toFixed(1)} cmH₂O`, 20, yPos);
      yPos += 5;
      doc.text(`Percentual do predito: ${percentPredicted.toFixed(1)}%`, 20, yPos);
      yPos += 5;

      // Classificação
      let classification = '';
      let classColor: [number, number, number] = [0, 0, 0];
      if (percentPredicted >= 80) {
        classification = 'Normal (≥ 80% do predito)';
        classColor = [40, 167, 69];
      } else if (percentPredicted >= 60) {
        classification = 'Fraqueza leve (60-79% do predito)';
        classColor = [255, 193, 7];
      } else if (percentPredicted >= 40) {
        classification = 'Fraqueza moderada (40-59% do predito)';
        classColor = [255, 152, 0];
      } else {
        classification = 'Fraqueza grave (< 40% do predito)';
        classColor = [220, 53, 69];
      }

      doc.setTextColor(classColor[0], classColor[1], classColor[2]);
      doc.setFont('helvetica', 'bold');
      doc.text(`Classificação: ${classification}`, 20, yPos);

      yPos += 15;
    }

    // Medidas individuais
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Medidas Realizadas:', 15, yPos);
    yPos += 10;

    // Tabela de medidas
    doc.setFillColor(240, 240, 240);
    doc.rect(15, yPos - 5, pageWidth - 30, 8, 'F');
    doc.setFontSize(9);
    doc.text('Medida', 20, yPos);
    doc.text('PImax (cmH₂O)', 80, yPos);
    doc.text('S-Index (cmH₂O)', 140, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'normal');
    const pimaxMeasurements = pimaxData.pimax?.measurements || [];
    const snipMeasurements = pimaxData.sindex?.measurements || [];
    const maxMeasurements = Math.max(pimaxMeasurements.length, snipMeasurements.length);

    for (let i = 0; i < maxMeasurements; i++) {
      if (i % 2 === 1) {
        doc.setFillColor(250, 250, 250);
        doc.rect(15, yPos - 5, pageWidth - 30, 7, 'F');
      }

      doc.text(`${i + 1}ª medida`, 20, yPos);
      doc.text(pimaxMeasurements[i]?.toFixed(1) || '-', 80, yPos);
      doc.text(snipMeasurements[i]?.toFixed(1) || '-', 140, yPos);
      yPos += 7;
    }

    yPos += 10;

    // Interpretação
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Interpretação:', 15, yPos);
    yPos += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const interpretation = [
      'A PImax e o S-Index avaliam a força dos músculos respiratórios.',
      '',
      '• PImax: Mede a força dos músculos inspiratórios',
      '• S-Index (SNIP): Avalia a pressão inspiratória nasal durante o "sniff"',
      '',
      'Valores reduzidos podem indicar fraqueza muscular respiratória,',
      'comum em doenças neuromusculares, DPOC avançada e outras condições.',
      '',
      'Valores normais variam com idade, sexo e características antropométricas.'
    ];
    interpretation.forEach(line => {
      doc.text(line, 15, yPos);
      yPos += 5;
    });

    yPos += 10;

    // Evolução temporal
    if (previousAssessments && previousAssessments.length > 0) {
      if (yPos > pageHeight - 80) {
        doc.addPage();
        yPos = 20;
      }

      const pimaxHistory: HistoricalDataEntry[] = [];
      const snipHistory: HistoricalDataEntry[] = [];
      
      previousAssessments
        .filter(a => a.type === 'PIMAX_SINDEX' && a.data?.PIMAX_SINDEX)
        .slice(-5)
        .forEach(a => {
          const date = new Date(a.datetime || a.createdAt);
          const dateLabel = date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
          pimaxHistory.push({ label: dateLabel, value: a.data?.PIMAX_SINDEX?.pimax?.max || 0 });
          snipHistory.push({ label: dateLabel, value: a.data?.PIMAX_SINDEX?.sindex?.max || 0 });
        });

      const currentDate = new Date(assessment.datetime || assessment.createdAt);
      const currentDateLabel = currentDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
          pimaxHistory.push({ label: currentDateLabel, value: maxPimax, color: [220, 53, 69] });
          snipHistory.push({ label: currentDateLabel, value: maxSnip, color: [0, 123, 255] });

      // Gráfico PImax
      this.drawBarChart(
        doc,
        15,
        yPos,
        (pageWidth - 35) / 2,
        50,
        pimaxHistory,
        'Evolução - PImax',
        'cmH₂O'
      );

      // Gráfico S-Index
        this.drawBarChart(
          doc,
          (pageWidth / 2) + 5,
          yPos,
          (pageWidth - 35) / 2,
          50, // width
          snipHistory,
          'Evolução - S-Index',
          'cmH₂O', // yAxisLabel
          undefined // yMax
        );

      yPos += 70;
    }

    if (assessment.notes) {
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = 20;
      }

      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Observações:', 15, yPos);
      yPos += 7;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const notesLines = doc.splitTextToSize(assessment.notes, pageWidth - 30);
      doc.text(notesLines, 15, yPos);
    }

    this.renderFooter(doc, 1, 1);
    return doc;
  }

  /**
   * Gera relatório completo do CPET - VERSÃO FINAL OTIMIZADA
   */
  private static async generateCPETReport(
    doc: jsPDF,
    assessment: Assessment,
    previousAssessments?: Assessment[]
  ): Promise<jsPDF> {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 15;
    const accentColor: [number, number, number] = [37, 99, 235];

    const checkSpace = (needed: number, forceNewPage: boolean = false) => {
      if (forceNewPage || yPosition + needed > pageHeight - 30) {
        doc.addPage();
        yPosition = 20;
        return true;
      }
      return false;
    };

    yPosition = await this.renderHeader(doc, yPosition);
    yPosition += 5;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...accentColor);
    this.drawText(
      doc,
      'LAUDO DE TESTE CARDIOPULMONAR DE ESFORÇO (TCPE)',
      pageWidth / 2,
      yPosition,
      'center'
    );
    yPosition += 12;

    const cpetData = assessment.data?.CPET || assessment.data;
    const summary = cpetData?.summary;

    if (!summary) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(30, 41, 59);
      this.drawText(doc, 'Dados do teste não disponíveis', 15, yPosition);

      const totalPagesFallback = doc.getNumberOfPages();
      for (let i = 1; i <= totalPagesFallback; i++) {
        doc.setPage(i);
        this.renderFooter(doc, i, totalPagesFallback);
      }

      return doc;
    }

    const interpretation = this.interpretCPET(cpetData);
    const finalSample = cpetData.series?.[cpetData.series.length - 1] || {};
    const formatNumber = (value: number | null | undefined, decimals: number) =>
      typeof value === 'number' && !Number.isNaN(value) ? value.toFixed(decimals) : 'N/A';

    const predictedHR =
      typeof cpetData?.patientAge === 'number' ? 220 - cpetData.patientAge : undefined;
    const hrPercentPredicted =
      predictedHR && predictedHR > 0 ? (summary.hr_peak / predictedHR) * 100 : undefined;

    const classificationColor: [number, number, number] =
      interpretation.functionalCapacity.classification === 'Normal'
        ? [34, 197, 94]
        : interpretation.functionalCapacity.classification === 'Levemente reduzida'
        ? [234, 179, 8]
        : interpretation.functionalCapacity.classification === 'Moderadamente reduzida'
        ? [249, 115, 22]
        : [239, 68, 68];

    const metricCards: MetricCard[] = [
      {
        label: 'VO₂ PICO',
        value: `${formatNumber(summary.vo2peak_lmin, 2)} L/min`,
        details: `${formatNumber(summary.vo2peak_mlkg, 1)} mL/kg/min`
      },
      {
        label: '% VO₂ PREDITO',
        value: `${interpretation.functionalCapacity.percentPredicted.toFixed(0)}%`,
        details: 'Capacidade aeróbica'
      },
      {
        label: 'CLASSIFICAÇÃO',
        value: interpretation.functionalCapacity.classification,
        details: `${interpretation.functionalCapacity.percentPredicted.toFixed(0)}% do predito`,
        accentColor: classificationColor
      },
      {
        label: 'FC PICO',
        value: `${summary.hr_peak} bpm`,
        details: hrPercentPredicted
          ? `${hrPercentPredicted.toFixed(0)}% da FC máxima estimada`
          : 'Resposta cronotrópica'
      },
      {
        label: 'VE/VCO₂ SLOPE',
        value: formatNumber(summary.vevco2_slope, 1),
        details: 'Eficiência ventilatória'
      },
      {
        label: 'RER PICO',
        value: formatNumber((finalSample as any).RER, 2),
        details: 'Esforço atingido'
      }
    ];

    checkSpace(80);
    yPosition = this.renderMetricCards(doc, yPosition, metricCards);
    yPosition += 4;
    doc.setTextColor(30, 41, 59);

    const examDate = assessment.datetime ? new Date(assessment.datetime) : null;
    const validExamDate = examDate && !Number.isNaN(examDate.getTime());
    const examDateLabel = validExamDate
      ? `${examDate.toLocaleDateString('pt-BR')} às ${examDate.toLocaleTimeString('pt-BR', {
          hour: '2-digit',
          minute: '2-digit'
        })}`
      : 'N/A';

    const sexLabel =
      cpetData?.patientSex === 'M'
        ? 'Masculino'
        : cpetData?.patientSex === 'F'
        ? 'Feminino'
        : 'N/A';

    const protocolInfo =
      cpetData?.protocol || summary?.protocol || cpetData?.testProtocol || 'N/A';
    const modalityInfo =
      cpetData?.exerciseModality ||
      cpetData?.modality ||
      cpetData?.equipment ||
      cpetData?.device ||
      '';

    const leftItems: InfoColumnItem[] = [
      { label: 'Paciente', value: assessment.patientName },
      { label: 'Prontuário', value: assessment.patientProntuario || 'N/A' },
      {
        label: 'Idade / Sexo',
        value: `${cpetData?.patientAge || 'N/A'} anos | ${sexLabel}`
      },
      {
        label: 'Antropometria',
        value: `Altura: ${cpetData?.patientHeight || 'N/A'} cm | Peso: ${
          cpetData?.patientWeight || 'N/A'
        } kg`
      }
    ];

    const rightItems: InfoColumnItem[] = [
      { label: 'Data do Exame', value: examDateLabel },
      {
        label: 'Médico Responsável',
        value: `${assessment.doctorName || 'N/A'}${
          assessment.doctorRegistration ? ` | CRM: ${assessment.doctorRegistration}` : ''
        }`
      },
      { label: 'Protocolo', value: protocolInfo || 'N/A' }
    ];

    if (modalityInfo) {
      rightItems.push({ label: 'Modalidade / Equipamento', value: modalityInfo });
    }

    yPosition = this.renderInfoColumns(doc, yPosition, leftItems, rightItems);
    yPosition += 4;

    checkSpace(30);
    yPosition = this.renderSectionTitle(doc, yPosition, 'Resultados Principais');

    const resultBlocks = [
      {
        title: 'Capacidade Aeróbica',
        lines: [
          `VO₂ pico: ${formatNumber(summary.vo2peak_lmin, 2)} L/min (${formatNumber(
            summary.vo2peak_mlkg,
            1
          )} mL/kg/min)`,
          `Equivalente a ${interpretation.functionalCapacity.percentPredicted.toFixed(
            0
          )}% do valor predito`
        ]
      },
      {
        title: 'Eficiência Cardiovascular',
        lines: [
          `Pulso O₂ pico: ${formatNumber(summary.o2pulse_peak_ml, 1)} mL/bat`,
          hrPercentPredicted
            ? `FC pico: ${summary.hr_peak} bpm (${hrPercentPredicted.toFixed(0)}% da predita)`
            : `FC pico: ${summary.hr_peak} bpm`
        ]
      },
      {
        title: 'Eficiência Ventilatória',
        lines: [
          `VE/VCO₂ slope: ${formatNumber(summary.vevco2_slope, 1)} | OUES: ${formatNumber(
            summary.oues,
            0
          )}`,
          `VE pico: ${formatNumber((finalSample as any).VE, 1)} L/min`
        ]
      }
    ];

    resultBlocks.forEach(block => {
      const content = block.lines.filter(Boolean) as string[];
      if (!content.length) {
        return;
      }

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const lineCount = content.reduce(
        (acc, line) => acc + doc.splitTextToSize(line, pageWidth - 40).length,
        0
      );
      const blockHeight = 12 + lineCount * 4;

      checkSpace(blockHeight + 6);

      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(15, yPosition, pageWidth - 30, blockHeight, 2, 2, 'FD');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...accentColor);
      this.drawText(doc, block.title, 20, yPosition + 7);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(30, 41, 59);

      let textY = yPosition + 14;
      content.forEach(line => {
        const splitLines = doc.splitTextToSize(line, pageWidth - 40);
        splitLines.forEach((splitLine: string, index: number) => {
          const bulletLine = index === 0 ? `• ${splitLine}` : splitLine;
          this.drawText(doc, bulletLine, 22, textY);
          textY += 4;
        });
        textY += 1.5;
    });

    yPosition += blockHeight + 8;
  });

  // ============================================================================
  // AVALIAÇÃO DO ESFORÇO
  // ============================================================================
  checkSpace(40);
  const effortBoxWidth = pageWidth - 30;
  const effortLines: string[] = [];
  const veAtVT1PercentDisplay =
    summary.ve_vt1 != null && summary.ve_peak != null
      ? (summary.ve_vt1 / summary.ve_peak) * 100
      : null;
  const maxState = interpretation.maximalTest.isMaximal;
  const abnormalState = interpretation.abnormalResponse.present;

  const translateState = (value: boolean | null): string => {
    if (value === null) return 'Não avaliado (dados insuficientes)';
    return value ? 'Sim' : 'Não';
  };

  const maximalCriteriaDisplay = interpretation.maximalTest.criteriaMet || [];
  const abnormalCriteriaDisplay = interpretation.abnormalResponse.criteriaMet || [];

  effortLines.push(`Teste máximo: ${translateState(maxState)}`);
  if (maximalCriteriaDisplay.length) {
    effortLines.push('  Critérios atendidos:');
    maximalCriteriaDisplay.forEach(c => effortLines.push(`    - ${c}`));
  }

  effortLines.push('');
  effortLines.push(`Resposta anormal ao exercício: ${translateState(abnormalState)}`);
  if (abnormalCriteriaDisplay.length) {
    effortLines.push('  Evidências:');
    abnormalCriteriaDisplay.forEach(c => effortLines.push(`    - ${c}`));
  }

  if (summary.spo2_drop != null || summary.ve_mvv_ratio != null || veAtVT1PercentDisplay != null) {
    effortLines.push('');
    if (summary.spo2_drop != null) {
      effortLines.push(`  • Variação de SpO₂: ${summary.spo2_drop >= 0 ? '-' : ''}${summary.spo2_drop.toFixed(1)}%`);
    }
    if (summary.ve_mvv_ratio != null) {
      effortLines.push(`  • VE pico / MVV predita: ${(summary.ve_mvv_ratio * 100).toFixed(0)}%`);
    }
    if (veAtVT1PercentDisplay != null) {
      effortLines.push(`  • VE @ VT1: ${veAtVT1PercentDisplay.toFixed(0)}% do VE pico`);
    }
  }

  if (interpretation.terminationNotes) {
    effortLines.push('');
    effortLines.push(`Observação de terminação: ${interpretation.terminationNotes}`);
  }

  const effortWrapped = effortLines.flatMap(line => {
    if (line === '') return [''];
    return doc.splitTextToSize(line, effortBoxWidth - 10);
  });
  const effortHeight = 12 + effortWrapped.length * 4;
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(15, yPosition, effortBoxWidth, effortHeight, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(37, 99, 235);
  this.drawText(doc, 'Avaliação do esforço', 20, yPosition + 7);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(30, 41, 59);

  let effortY = yPosition + 14;
  effortWrapped.forEach(line => {
    if (line === '') {
      effortY += 2;
    } else {
      this.drawText(doc, line, 20, effortY);
      effortY += 4;
    }
  });

  yPosition += effortHeight + 10;

  checkSpace(30);
  yPosition = this.renderSectionTitle(doc, yPosition, 'Interpretação Clínica');

  checkSpace(70);
  doc.setFillColor(...classificationColor);
    doc.setDrawColor(...classificationColor);
    doc.roundedRect(15, yPosition, pageWidth - 30, 20, 3, 3, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    this.drawText(
      doc,
      `Capacidade Funcional: ${interpretation.functionalCapacity.classification}`,
      pageWidth / 2,
      yPosition + 9,
      'center'
    );

    doc.setFontSize(10);
    doc.setTextColor(233, 233, 233);
    this.drawText(
      doc,
      `${interpretation.functionalCapacity.percentPredicted.toFixed(0)}% do valor predito`,
      pageWidth / 2,
      yPosition + 15,
      'center'
    );

    yPosition += 28;

    const descLines = doc.splitTextToSize(
      interpretation.functionalCapacity.description,
      pageWidth - 30
    );
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(30, 41, 59);
    descLines.forEach((line: string) => {
      checkSpace(10);
      this.drawText(doc, line, 15, yPosition);
      yPosition += 4.5;
    });

    yPosition += 6;

    const limitationLines = doc.splitTextToSize(
      interpretation.primaryLimitation,
      pageWidth - 40
    );
    const limitationHeight = 14 + limitationLines.length * 4;

    checkSpace(limitationHeight + 6);
    doc.setFillColor(240, 249, 255);
    doc.setDrawColor(191, 219, 254);
    doc.roundedRect(15, yPosition, pageWidth - 30, limitationHeight, 2, 2, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(37, 99, 235);
    this.drawText(doc, 'Limitação Primária', 20, yPosition + 7);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(30, 41, 59);
    let limitationY = yPosition + 14;
    limitationLines.forEach((line: string) => {
      this.drawText(doc, line, 22, limitationY);
      limitationY += 4;
    });

    yPosition += limitationHeight + 8;

    const evidenceSections: Array<{
      title: string;
      items: string[];
      fill: [number, number, number];
      border: [number, number, number];
    }> = [];

    if (interpretation.limitations.cardiovascular.present) {
      evidenceSections.push({
        title: `Cardiovascular (${interpretation.limitations.cardiovascular.severity})`,
        items: interpretation.limitations.cardiovascular.evidence,
        fill: [254, 242, 242],
        border: [239, 68, 68]
      });
    }

    if (interpretation.limitations.ventilatory.present) {
      evidenceSections.push({
        title: `Ventilatória (${interpretation.limitations.ventilatory.severity})`,
        items: interpretation.limitations.ventilatory.evidence,
        fill: [239, 246, 255],
        border: [59, 130, 246]
      });
    }

    if (interpretation.limitations.peripheral.present) {
      evidenceSections.push({
        title: `Periférica/Muscular (${interpretation.limitations.peripheral.severity})`,
        items: interpretation.limitations.peripheral.evidence,
        fill: [240, 253, 244],
        border: [34, 197, 94]
      });
    }

    if (interpretation.limitations.deconditioning.present) {
      evidenceSections.push({
        title: 'Descondicionamento',
        items: interpretation.limitations.deconditioning.evidence,
        fill: [255, 251, 235],
        border: [234, 179, 8]
      });
    }

    evidenceSections.forEach(section => {
      const evidences = section.items.length ? section.items : ['Sem observações adicionais'];
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const splittedEvidence = evidences.map(evidence =>
        doc.splitTextToSize(evidence, pageWidth - 44)
      );
      const contentLines = splittedEvidence.reduce((acc, lines) => acc + lines.length, 0);
      const boxHeight = 12 + contentLines * 4 + evidences.length * 1.5;

      checkSpace(boxHeight + 6);

      doc.setFillColor(...section.fill);
      doc.setDrawColor(...section.border);
      doc.roundedRect(15, yPosition, pageWidth - 30, boxHeight, 2, 2, 'FD');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...section.border);
      this.drawText(doc, section.title, 20, yPosition + 7);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(30, 41, 59);

      let evidenceY = yPosition + 14;
      splittedEvidence.forEach(lines => {
        lines.forEach((line: string, lineIdx: number) => {
          const textLine = lineIdx === 0 ? `• ${line}` : line;
          this.drawText(doc, textLine, 22, evidenceY);
          evidenceY += 4;
        });
        evidenceY += 1.5;
      });

      yPosition += boxHeight + 6;
    });

    if (cpetData.chartImages && Object.keys(cpetData.chartImages).length > 0) {
      doc.addPage();
      yPosition = 20;

      checkSpace(30);
      yPosition = this.renderSectionTitle(doc, yPosition, 'Painéis de Wasserman');
      yPosition += 4;

      const columns = 3;
      const marginX = 8;
      const marginY = 8;
      const usableWidth = pageWidth - 30;
      const chartWidth = (usableWidth - marginX * (columns - 1)) / columns;
      const chartHeight = chartWidth * 0.75;
      const startX = 15;
      const startY = yPosition;

      const panelKeys = [
        'panel1',
        'panel2',
        'panel3',
        'panel4',
        'panel5',
        'panel6',
        'panel7',
        'panel8',
        'panel9'
      ];

      panelKeys.forEach((key, index) => {
        const imageData = cpetData.chartImages[key];
        if (imageData) {
          const row = Math.floor(index / 3);
          const col = index % 3;

          const x = startX + col * (chartWidth + marginX);
          const y = startY + row * (chartHeight + marginY);

          doc.setDrawColor(226, 232, 240);
          doc.rect(x - 1, y - 1, chartWidth + 2, chartHeight + 2);

          try {
            doc.addImage(imageData, 'PNG', x, y, chartWidth, chartHeight);
          } catch (error) {
            console.error(`Erro ao adicionar gráfico ${key}:`, error);
          }
        }
      });

      doc.addPage();
      yPosition = 20;
    }

    checkSpace(30);
    yPosition = this.renderSectionTitle(doc, yPosition, 'Zonas de Treinamento');

    const zoneColors: [number, number, number][] = [
      [59, 130, 246],
      [34, 197, 94],
      [249, 115, 22],
      [217, 70, 239],
      [239, 68, 68]
    ];

    const trainingCards: MetricCard[] = Object.values(interpretation.trainingZones).map(
      (zone, index) => ({
        label: zone.name,
        value: zone.hrRange || 'N/A',
        details: zone.description,
        accentColor: zoneColors[index] || accentColor
      })
    );

    checkSpace(70);
    yPosition = this.renderMetricCards(doc, yPosition, trainingCards);
    yPosition += 4;

    checkSpace(30);
    yPosition = this.renderSectionTitle(doc, yPosition, 'Conclusão');

    const summaryBoxX = 15;
    const summaryBoxWidth = pageWidth - 30;
    const summaryHorizontalPadding = 10;
    const summaryTextWidth = summaryBoxWidth - summaryHorizontalPadding * 2;
    const summaryLines = doc.splitTextToSize(
      interpretation.clinicalSummary,
      summaryTextWidth
    );
    const summaryBoxHeight = 12 + summaryLines.length * 4;

    checkSpace(summaryBoxHeight + 6);
    doc.setFillColor(255, 247, 237);
    doc.setDrawColor(251, 191, 36);
    doc.roundedRect(summaryBoxX, yPosition, summaryBoxWidth, summaryBoxHeight, 2, 2, 'FD');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);

    let summaryY = yPosition + 10;
    const summaryTextX = summaryBoxX + summaryHorizontalPadding;
    summaryLines.forEach((line: string) => {
      this.drawText(doc, line, summaryTextX, summaryY);
      summaryY += 4;
    });

    yPosition += summaryBoxHeight + 10;

    checkSpace(30);
    yPosition = this.renderSectionTitle(doc, yPosition, 'Recomendações');

    const recommendations = interpretation.recommendations?.length
      ? interpretation.recommendations
      : ['Sem recomendações adicionais registradas.'];
    const recommendationLines = recommendations.map((rec, index) =>
      doc.splitTextToSize(`${index + 1}. ${rec}`, pageWidth - 40)
    );
    const recommendationLineCount = recommendationLines.reduce(
      (acc, lines) => acc + lines.length,
      0
    );
    const recommendationsBoxHeight = 12 + recommendationLineCount * 4;

    checkSpace(recommendationsBoxHeight + 6);
    doc.setFillColor(240, 249, 255);
    doc.setDrawColor(165, 180, 252);
    doc.roundedRect(15, yPosition, pageWidth - 30, recommendationsBoxHeight, 2, 2, 'FD');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(30, 41, 59);

    let recommendationY = yPosition + 10;
    recommendationLines.forEach(lines => {
      lines.forEach((line: string) => {
        this.drawText(doc, line, 20, recommendationY);
        recommendationY += 4;
      });
      recommendationY += 1.5;
    });

    yPosition += recommendationsBoxHeight + 10;

    if (assessment.notes) {
      checkSpace(30);
      yPosition = this.renderSectionTitle(doc, yPosition, 'Observações');

      const notesLines = doc.splitTextToSize(assessment.notes, pageWidth - 30);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(71, 85, 105);

      notesLines.forEach((line: string) => {
        checkSpace(10);
        this.drawText(doc, line, 15, yPosition);
        yPosition += 4.5;
      });

      yPosition += 6;
    }

    checkSpace(35);
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.4);
    doc.line(15, yPosition, 95, yPosition);
    yPosition += 5;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(30, 41, 59);
    this.drawText(doc, assessment.doctorName || 'Médico Responsável', 15, yPosition);
    yPosition += 5;

    if (assessment.doctorRegistration) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      this.drawText(doc, `CRM: ${assessment.doctorRegistration}`, 15, yPosition);
      yPosition += 5;
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    this.drawText(doc, `Data do laudo: ${new Date().toLocaleDateString('pt-BR')}`, 15, yPosition);

    this.renderCPETBibliography(doc);

    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      this.renderFooter(doc, i, totalPages);
    }
    doc.setPage(totalPages);

    return doc;
  }

  
  /**
   * Relatório para avaliações múltiplas (combina vários testes em um PDF)
   */
  private static async generateMultiTestReport(
    doc: jsPDF,
    assessment: Assessment,
    previousAssessments?: Assessment[]
  ): Promise<jsPDF> {
    const tests = assessment.tests || [];
    let isFirstTest = true;

    for (const testType of tests) {
      // Criar uma avaliação temporária para cada teste
      const singleTestAssessment: Assessment = {
        ...assessment,
        type: testType,
        tests: undefined,
      };

      // Filtrar avaliações anteriores do mesmo tipo
      const previousOfSameType = previousAssessments?.filter(a => a.type === testType) || [];

      // Se não for o primeiro teste, adicionar nova página
      if (!isFirstTest) {
        doc.addPage();
      }

      // Gerar o relatório específico para este tipo de teste
      await this.generateSingleTestInMulti(doc, singleTestAssessment, previousOfSameType, isFirstTest);
      
      isFirstTest = false;
    }

    return doc;
  }

  /**
   * Gera um único teste dentro de um relatório múltiplo
   */
  private static async generateSingleTestInMulti(
    doc: jsPDF,
    assessment: Assessment,
    previousAssessments: Assessment[],
    isFirstPage: boolean
  ): Promise<void> {
    // Salvar o número de páginas atual
    const currentPageCount = doc.getNumberOfPages();
    const startPage = isFirstPage ? 1 : currentPageCount;

    // Criar um documento temporário para este teste
    const tempDoc = new jsPDF();

    // Gerar o relatório específico
    let generatedDoc: jsPDF;
    
    switch (assessment.type) {
      case 'TC6':
        generatedDoc = await this.generateTC6Report(tempDoc, assessment, previousAssessments);
        break;
      case 'CAT':
        generatedDoc = await this.generateCATReport(tempDoc, assessment, previousAssessments);
        break;
      case 'mMRC':
        generatedDoc = await this.generateMMRCReport(tempDoc, assessment, previousAssessments);
        break;
      case 'PFSDQ-M':
        generatedDoc = await this.generatePFSDQMReport(tempDoc, assessment, previousAssessments);
        break;
      case '5xSTS':
      case 'STS_30S':
      case 'STS_1MIN':
        generatedDoc = await this.generateSTSReport(tempDoc, assessment, previousAssessments);
        break;
      case 'HADS':
        generatedDoc = await this.generateHADSReport(tempDoc, assessment, previousAssessments);
        break;
      case 'SGRQ':
        generatedDoc = await this.generateSGRQReport(tempDoc, assessment, previousAssessments);
        break;
      case 'LCADL':
        generatedDoc = await this.generateLCADLReport(tempDoc, assessment, previousAssessments);
        break;
      case 'MLHFQ':
        generatedDoc = await this.generateMLHFQReport(tempDoc, assessment, previousAssessments);
        break;
      case 'PIMAX_SINDEX':
        generatedDoc = await this.generatePImaxSIndexReport(tempDoc, assessment, previousAssessments);
        break;
      default:
        generatedDoc = await this.generateGenericReport(tempDoc, assessment);
        break;
    }

    // Copiar as páginas do documento temporário para o documento principal
    const tempPageCount = generatedDoc.getNumberOfPages();
    
    for (let i = 1; i <= tempPageCount; i++) {
      if (i === 1 && isFirstPage) {
        // Se for a primeira página do primeiro teste, usar a página existente
        const pageContent = (generatedDoc as any).internal.pages[i];
        (doc as any).internal.pages[1] = pageContent;
      } else {
        // Adicionar nova página e copiar conteúdo
        if (i > 1 || !isFirstPage) {
          doc.addPage();
        }
        const pageContent = (generatedDoc as any).internal.pages[i];
        const currentPage = doc.getNumberOfPages();
        (doc as any).internal.pages[currentPage] = pageContent;
      }
    }
  }
}
